import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { toast } from "sonner";
import { TabActividadChoferes } from "./SupervisorDashboard";
import "../styles/supervisor.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ZONAS = ["Z1","Z2","Z3","Z4","Z5","Z6","Z7","Z8","Z9","Z10","Z11","Z12","Z13","Z14"];

const TABS = [
  { k: "inicio",      label: "Inicio",                       icon: "fa-house" },
  { k: "excel",       label: "Subir Excel CRC",              icon: "fa-file-excel" },
  { k: "captura",     label: "Captura de actividad",         icon: "fa-truck-droplet" },
  { k: "historial",   label: "Historial y evidencias",       icon: "fa-folder-open" },
  { k: "actividad",   label: "Actividad de Choferes",        icon: "fa-user-clock" },
  { k: "parques",     label: "Parques Contratados 2026",      icon: "fa-map-location-dot" },
];

const ESTATUS = {
  PENDIENTE_SUPERVISOR: { lbl: "Pendiente",  bg: "#FEF3C7", color: "#92400E", icon: "fa-hourglass-half" },
  DUDOSO_IA:            { lbl: "Dudoso IA",  bg: "#FEE2E2", color: "#991B1B", icon: "fa-circle-exclamation" },
  ASIGNADA:             { lbl: "Asignada",   bg: "#DBEAFE", color: "#1E40AF", icon: "fa-user-check" },
  EN_RUTA:              { lbl: "En ruta",    bg: "#DBEAFE", color: "#1E40AF", icon: "fa-truck" },
  ATENDIDO:             { lbl: "Atendido",   bg: "#D1FAE5", color: "#047857", icon: "fa-check" },
  NO_PUDO_ATENDER:      { lbl: "No se pudo", bg: "#FEE2E2", color: "#991B1B", icon: "fa-ban" },
  CANCELADO:            { lbl: "Cancelado",  bg: "#F1F5F9", color: "#475569", icon: "fa-xmark" },
  DECLINADO:            { lbl: "Declinado",  bg: "#F3F4F6", color: "#6B7280", icon: "fa-ban" },
};

/** True si el CRC fue reasignado automáticamente y aún está pendiente de asignar. */
const esReasignado = (s) =>
  s.estatus === "PENDIENTE_SUPERVISOR" &&
  Array.isArray(s.reasignaciones) &&
  s.reasignaciones.length > 0;

const ORIGEN_BG = {
  "Ordinario":      { bg: "#DCFCE7", color: "#166534" },
  "CRC":            { bg: "#DBEAFE", color: "#1E40AF" },
  "Oficio":         { bg: "#FEF3C7", color: "#92400E" },
  "Reporte Interno":{ bg: "#EDE9FE", color: "#6D28D9" },
};

// Orígenes para los que se ofrece "Compartir" (link del comprobante) en el listado.
// "Ordinario" queda fuera a propósito (actividad rutinaria, sin folio de CRC/oficio formal).
const ORIGENES_CON_COMPARTIR = ["CRC", "Oficio", "Reporte Interno"];

/** URL pública del comprobante de atención para un folio dado. */
function _linkComprobante(s) {
  const base = process.env.REACT_APP_PUBLIC_URL || "https://dmu614.com";
  return `${base}/evidencia/${encodeURIComponent(s.folio || "")}`;
}

/** Copia el link del comprobante al portapapeles.
 *  Usa navigator.clipboard cuando está disponible; si no, fallback con textarea. */
async function _copiarLinkComprobante(s) {
  const url = _linkComprobante(s);
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      await navigator.clipboard.writeText(url);
    } else {
      // Fallback para navegadores sin Clipboard API
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (!ok) throw new Error("execCommand falló");
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}

export default function CapturistaModule() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inicio");
  const [solicitudes, setSolicitudes] = useState([]);
  const [stats, setStats] = useState(null);
  const [modalDoc, setModalDoc] = useState(null);

  const recargar = useCallback(async () => {
    const [a, b] = await Promise.all([
      axios.get(`${API}/capturista/solicitudes`),
      axios.get(`${API}/capturista/stats`),
    ]);
    setSolicitudes(a.data);
    setStats(b.data);
  }, []);

  useEffect(() => { recargar(); }, [recargar]);

  const dudosos = solicitudes.filter(s =>
    ["DUDOSO_IA","SIN_GPS","DUDOSO_GPS","DUDOSO_ZONA"].includes(s.estatus)
  );
  const limpios = solicitudes.filter(s => s.estatus !== "DUDOSO_IA" && s.estatus !== "DECLINADO");
  const [solAEditar, setSolAEditar] = useState(null);
  const [solADeclinar, setSolADeclinar] = useState(null);
  const [solACerrar, setSolACerrar] = useState(null);

  // Permisos eliminar (admin + capturista). El backend reconfirma.
  const puedeEliminar = ["admin", "capturista"].includes(user?.rol);

  const declinarSolicitud = async (s, motivo, comentario) => {
    try {
      await axios.post(`${API}/capturista/solicitud/${s.id}/declinar`, {
        motivo_declinacion: motivo,
        comentario_declinacion: comentario || null,
      });
      setSolicitudes(prev => prev.map(x => x.id === s.id ? { ...x, estatus: "DECLINADO" } : x));
      setSolADeclinar(null);
      toast.success(`Folio ${s.folio} declinado`);
    } catch (e) {
      toast.error("No se pudo declinar: " + (e.response?.data?.detail || e.message));
    }
  };

  const eliminarSolicitud = async (s) => {
    if (!puedeEliminar) return;
    const ok = window.confirm(
      `¿Eliminar el folio ${s.folio}? Esta acción no se puede deshacer.`
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/capturista/solicitud/${s.id}`);
      // Quitar de la lista sin recargar
      setSolicitudes(prev => prev.filter(x => x.id !== s.id));
      toast.success(`Folio ${s.folio} eliminado`);
    } catch (e) {
      toast.error("No se pudo eliminar, intenta de nuevo");
    }
  };

  return (
    <div className="hub-bg" data-testid="page-capturista">
      <header className="topnav">
        <div className="brand">
          <button onClick={() => navigate("/hub")} className="iconbtn mr-1" aria-label="Atrás" data-testid="btn-back-hub-cap">
            <i className="fa-solid fa-arrow-left"/>
          </button>
          <div className="logo" style={{background: "var(--dep-pipas)"}}><i className="fa-solid fa-file-pen"/></div>
          <div>
            <div className="title">Captura · Pipas</div>
            <div className="sub">Atención Ciudadana · Oficios · Reportes</div>
          </div>
        </div>
        <div className="actions">
          <div className="hidden sm:block text-right mr-2">
            <div className="text-sm font-semibold leading-tight">{user?.nombre}</div>
            <div className="text-xs text-slate-500">Capturista de Pipas</div>
          </div>
          <button onClick={logout} className="btn-ghost" data-testid="btn-logout-cap" style={{padding:"0.5rem 0.9rem"}}>
            <i className="fa-solid fa-right-from-bracket mr-1"/>Salir
          </button>
        </div>
      </header>

      {/* TABS */}
      <div className="cap-tabbar" data-testid="cap-tabbar">
        <div className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.k} className={`cap-tab ${tab===t.k?"active":""}`} onClick={() => setTab(t.k)} data-testid={`cap-tab-${t.k}`}>
              <i className={`fa-solid ${t.icon}`}/>
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      <main className={`max-w-7xl mx-auto px-4 py-6 ${tab === "actividad" || tab === "parques" ? "" : "grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6"}`}>
        <div>
          {tab === "inicio" && (
            <InicioTab
              stats={stats}
              limpios={limpios}
              onVerDoc={setModalDoc}
              onRecargar={recargar}
              onEliminar={puedeEliminar ? eliminarSolicitud : null}
              onDeclinar={puedeEliminar ? setSolADeclinar : null}
              onCerrar={setSolACerrar}
            />
          )}
          {tab === "captura"   && <TabCapturaUnificada onCreada={() => recargar()}/>}
          {tab === "excel"     && <ImportExcelTab onImported={() => recargar()} stats={stats}/>}
          {tab === "historial"  && <HistorialTab solicitudes={solicitudes}/>}
          {tab === "actividad"  && <TabActividadChoferes user={user}/>}
          {tab === "parques"    && <TabParquesContratos />}
        </div>

        {tab !== "actividad" && tab !== "parques" && (
          <aside data-testid="alertas-side">
            <AlertasIA dudosos={dudosos} onCorregir={setSolAEditar} onEliminar={puedeEliminar ? eliminarSolicitud : null} onDeclinar={puedeEliminar ? setSolADeclinar : null}/>
            <TareasHoy stats={stats}/>
          </aside>
        )}
      </main>

      {modalDoc && <ModalDoc doc={modalDoc} onClose={() => setModalDoc(null)}/>}
      {solAEditar && (
        <ModalCorregirUbicacion
          solicitud={solAEditar}
          onClose={() => setSolAEditar(null)}
          onSaved={() => { setSolAEditar(null); recargar(); }}
        />
      )}
      {solADeclinar && (
        <ModalDeclinar
          solicitud={solADeclinar}
          onClose={() => setSolADeclinar(null)}
          onConfirm={declinarSolicitud}
        />
      )}
      {solACerrar && (
        <ModalCerrar
          solicitud={solACerrar}
          onClose={() => setSolACerrar(null)}
          onCerrada={(id, estatus) => {
            setSolicitudes(prev => prev.map(x => x.id === id ? { ...x, estatus } : x));
            setSolACerrar(null);
            recargar();
          }}
        />
      )}
    </div>
  );
}

// ─── TAB: INICIO ────────────────────────────────────────────
function InicioTab({ stats, limpios, onVerDoc, onRecargar, onEliminar, onDeclinar, onCerrar }) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const visibles = useMemo(
    () => q ? limpios.filter(s => (s.folio || "").toLowerCase().includes(q)) : limpios,
    [limpios, q]
  );
  const sinResultados = q && visibles.length === 0;

  return (
    <>
      <section className="card-base" data-testid="listado-servicios">
        <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
          <div>
            <div className="text-lg font-bold">Listado de Servicios — Gestión y Seguimiento</div>
            <div className="text-xs text-slate-500">
              Últimas 24 horas ·{" "}
              {q
                ? `${visibles.length} de ${limpios.length} solicitudes (filtrado)`
                : `${limpios.length} solicitudes`}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative" data-testid="buscador-folio-wrap">
              <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"/>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por folio (ej. CRC683976)"
                aria-label="Buscar por folio"
                data-testid="input-buscar-folio"
                className="text-sm border border-slate-300 rounded-md pl-8 pr-8 py-1.5 w-64 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Limpiar búsqueda"
                  data-testid="btn-limpiar-busqueda"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs px-1"
                >
                  <i className="fa-solid fa-xmark"/>
                </button>
              )}
            </div>
            <button className="btn-ghost text-sm" onClick={onRecargar} data-testid="btn-recargar"><i className="fa-solid fa-arrows-rotate mr-1"/>Recargar</button>
          </div>
        </div>

        {sinResultados ? (
          <div
            className="py-10 text-center text-sm text-slate-500"
            data-testid="msg-sin-resultados-folio"
          >
            <i className="fa-solid fa-circle-info text-slate-400 mr-1"/>
            No se encontró el folio
            <div className="text-xs text-slate-400 mt-1">"{query}"</div>
          </div>
        ) : (
          <TablaSolicitudes solicitudes={visibles} onVerDoc={onVerDoc} onEliminar={onEliminar} onDeclinar={onDeclinar} onCerrar={onCerrar}/>
        )}
      </section>
    </>
  );
}

function ModalEditarCiudadano({ sol, onClose, onGuardado }) {
  const [nombre, setNombre] = useState(sol.ciudadano_nombre || "");
  const [tel, setTel] = useState(sol.ciudadano_telefono || "");
  const [coment, setComent] = useState(sol.comentarios || sol.titulo || "");
  const [saving, setSaving] = useState(false);
  const guardar = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/capturista/solicitud/${sol.id}/ciudadano`, {
        ciudadano_nombre: nombre,
        ciudadano_telefono: tel,
        comentarios: coment,
      });
      toast.success("Datos actualizados");
      onGuardado({ ciudadano_nombre: nombre, ciudadano_telefono: tel, comentarios: coment });
      onClose();
    } catch (err) {
      toast.error("Error: " + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.45)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }}
         onClick={onClose}>
      <div style={{ background:"white", borderRadius:12, padding:24, width:"min(420px,92vw)", boxShadow:"0 8px 32px rgba(0,0,0,.18)" }}
           onClick={e => e.stopPropagation()}>
        <div className="font-bold text-base mb-4">Editar datos ciudadano — <span className="mono text-sm text-slate-500">{sol.folio}</span></div>
        <div className="field mb-3">
          <label className="text-xs font-semibold text-slate-600 block mb-1">Nombre</label>
          <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" value={nombre} onChange={e=>setNombre(e.target.value)} placeholder="Juan Pérez"/>
        </div>
        <div className="field mb-3">
          <label className="text-xs font-semibold text-slate-600 block mb-1">Teléfono</label>
          <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" value={tel} onChange={e=>setTel(e.target.value)} placeholder="614 123 4567"/>
        </div>
        <div className="field mb-4">
          <label className="text-xs font-semibold text-slate-600 block mb-1">Comentarios / Descripción</label>
          <textarea className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" rows={3} value={coment} onChange={e=>setComent(e.target.value)} style={{resize:"vertical"}}/>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50" style={{background:"white"}}>Cancelar</button>
          <button onClick={guardar} disabled={saving} className="text-sm px-4 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-700">
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

const _ESTATUS_CERRABLES_FE = new Set([
  "PENDIENTE_SUPERVISOR","DUDOSO_IA","SIN_GPS","DUDOSO_GPS","DUDOSO_ZONA","ASIGNADA","EN_RUTA",
]);

function TablaSolicitudes({ solicitudes: initialSolicitudes, onVerDoc, onEliminar, onDeclinar, onCerrar }) {
  const [solicitudes, setSolicitudes] = useState(initialSolicitudes);
  const [expandidos, setExpandidos] = useState({});
  const [editando, setEditando] = useState(null);
  const toggleExpand = (id) => setExpandidos(prev => ({ ...prev, [id]: !prev[id] }));
  // Sync si el padre recarga
  useEffect(() => { setSolicitudes(initialSolicitudes); }, [initialSolicitudes]);
  if (!solicitudes.length) {
    return (
      <div className="py-12 text-center text-slate-400" data-testid="tabla-vacia">
        <i className="fa-solid fa-inbox text-4xl mb-3 text-slate-300"/>
        <div className="text-sm">No hay solicitudes en las últimas 24 horas.</div>
        <div className="text-xs mt-1">Importa un Excel o crea una nueva manualmente.</div>
      </div>
    );
  }
  const onGuardadoCiudadano = (campos) => {
    setSolicitudes(prev => prev.map(s => s.id === editando.id ? { ...s, ...campos } : s));
  };
  return (
    <>
    {editando && <ModalEditarCiudadano sol={editando} onClose={() => setEditando(null)} onGuardado={onGuardadoCiudadano}/>}
    <div className="overflow-x-auto -mx-5">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left p-3">Folio / Origen</th>
            <th className="text-left p-3">Lugar / Coordenadas</th>
            <th className="text-left p-3">Tipo agua</th>
            <th className="text-left p-3">Documento</th>
            <th className="text-left p-3">Zona / Pipa</th>
            <th className="text-left p-3">Estatus</th>
            <th className="text-left p-3">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {solicitudes.map(s => {
            const o = ORIGEN_BG[s.origen] || {bg:"#F1F5F9",color:"#0F172A"};
            const e = ESTATUS[s.estatus]   || {lbl:s.estatus, bg:"#F1F5F9", color:"#0F172A", icon:"fa-circle"};
            return (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`row-sol-${s.folio}`}>
                <td className="p-3 align-top">
                  <span className="origen-pill" style={{ background: o.bg, color: o.color }}>{s.origen}</span>
                  <div className="mono text-xs font-semibold mt-1">{s.folio}</div>
                  {s.ciudadano_nombre && (
                    <div className="text-xs text-slate-600 mt-1">
                      <i className="fa-solid fa-user fa-xs text-slate-400 mr-1"/>{s.ciudadano_nombre}
                    </div>
                  )}
                  {s.ciudadano_telefono && (
                    <div className="text-xs text-slate-500">
                      <i className="fa-solid fa-phone fa-xs text-slate-400 mr-1"/>{s.ciudadano_telefono}
                    </div>
                  )}
                </td>
                <td className="p-3 align-top">
                  <div className="font-medium text-slate-700 text-sm">{s.direccion_texto}</div>
                  {s.lat && s.lon ? (
                    <a href={`https://www.google.com/maps?q=${s.lat},${s.lon}`} target="_blank" rel="noreferrer" className="text-xs text-blue-700 mono hover:underline">
                      <i className="fa-solid fa-location-dot"/> {s.lat.toFixed(4)}, {s.lon.toFixed(4)}
                    </a>
                  ) : <div className="text-xs text-rose-600">Sin coords</div>}
                  {(s.comentarios || s.titulo) && (
                    <div className="mt-1">
                      <button
                        type="button"
                        onClick={() => toggleExpand(s.id)}
                        className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                      >
                        <i className={`fa-solid fa-chevron-${expandidos[s.id] ? "up" : "down"} fa-xs`}/>
                        {expandidos[s.id] ? "Ocultar comentarios" : "Ver comentarios"}
                      </button>
                      {expandidos[s.id] && (
                        <div className="mt-1 text-xs text-slate-600 bg-slate-50 rounded p-2 border border-slate-200 max-w-xs whitespace-pre-wrap">
                          {s.comentarios || s.titulo}
                        </div>
                      )}
                    </div>
                  )}
                </td>
                <td className="p-3 align-top">
                  <span className="text-xs font-semibold" style={{color: s.tipo_agua==="Tratada"?"#047857":"#1E40AF"}}>{s.tipo_agua}</span>
                </td>
                <td className="p-3 align-top">
                  {s.documento_nombre ? (
                    <button className="link-action" onClick={() => onVerDoc(s)} data-testid={`btn-ver-doc-${s.folio}`}>
                      <i className="fa-solid fa-file-pdf text-rose-600"/> Ver
                    </button>
                  ) : <span className="text-xs text-slate-400">—</span>}
                </td>
                <td className="p-3 align-top">
                  <div className="text-sm font-semibold">{s.zona_asignada || "—"}</div>
                  <div className="text-xs text-slate-500">{s.pipa_sugerida ? `Pipa ${s.pipa_sugerida}` : "Pipa por asignar"}</div>
                </td>
                <td className="p-3 align-top">
                  {esReasignado(s) ? (
                    <>
                      <span className="estatus-pill" style={{ background: "#FFF7ED", color: "#C2410C", border: "1.5px solid #FDBA74" }}
                            data-testid={`badge-reasignado-${s.folio}`}>
                        <i className="fa-solid fa-rotate"/> Reasignado
                      </span>
                      {(() => {
                        const ult = s.reasignaciones[s.reasignaciones.length - 1];
                        return (
                          <div className="mt-1 text-xs text-orange-700 opacity-80">
                            Antes: <strong>{ult.chofer_nombre || "chofer anterior"}</strong>
                            {ult.liberada_en && (
                              <span className="ml-1 opacity-70">
                                · {ult.liberada_en.slice(0, 10)}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    <span className="estatus-pill" style={{ background: e.bg, color: e.color }}>
                      <i className={`fa-solid ${e.icon}`}/> {e.lbl}
                    </span>
                  )}
                </td>
                <td className="p-3 align-top">
                  <div className="flex items-center gap-1.5 flex-nowrap">

                    {/* ── 1. CERRAR — protagonista, relieve 3D verde ── */}
                    {onCerrar && _ESTATUS_CERRABLES_FE.has(s.estatus) && (
                      <button
                        type="button"
                        onClick={() => onCerrar(s)}
                        data-testid={`btn-cerrar-${s.folio}`}
                        title={`Cerrar ${s.folio}`}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: "0.3rem",
                          padding: "11px 14px", borderRadius: 9,
                          border: "1.5px solid #6EE7B7", background: "white",
                          color: "#065F46", fontWeight: 700, fontSize: "0.8rem",
                          cursor: "pointer", whiteSpace: "nowrap",
                          boxShadow: "0 3px 0 #6EE7B7, 0 4px 8px rgba(16,185,129,0.15)",
                          transition: "all 0.15s",
                        }}
                      >
                        <i className="fa-solid fa-circle-check"/>Cerrar
                      </button>
                    )}

                    {/* ── 2. Grupo neutral: Mapa / Compartir / Editar ── */}
                    {(s.lat && s.lon) || ORIGENES_CON_COMPARTIR.includes(s.origen) ? (
                      <div className="flex items-center gap-1" style={{ borderLeft: "1px solid #E2E8F0", paddingLeft: "0.5rem", marginLeft: "0.25rem" }}>
                        {s.lat && s.lon && (
                          <a
                            href={`https://www.google.com/maps?q=${s.lat},${s.lon}`}
                            target="_blank" rel="noreferrer"
                            style={{
                              display: "inline-flex", alignItems: "center", gap: "0.3rem",
                              padding: "11px 10px", borderRadius: 9,
                              border: "1.5px solid #CBD5E1", background: "white",
                              color: "#475569", fontWeight: 600, fontSize: "0.78rem",
                              textDecoration: "none", whiteSpace: "nowrap",
                              boxShadow: "0 2px 0 #CBD5E1",
                              transition: "all 0.15s",
                            }}
                          >
                            <i className="fa-solid fa-map"/>Mapa
                          </a>
                        )}
                        {ORIGENES_CON_COMPARTIR.includes(s.origen) && s.folio && (
                          <button
                            type="button"
                            data-testid={`btn-compartir-${s.folio}`}
                            title={`Copiar link del comprobante ${s.folio}`}
                            onClick={async () => {
                              const res = await _copiarLinkComprobante(s);
                              if (res.ok) toast.success("Link copiado — listo para pegar en WhatsApp");
                              else toast.error("No se pudo copiar el link");
                            }}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: "0.3rem",
                              padding: "11px 10px", borderRadius: 9,
                              border: "1.5px solid #CBD5E1", background: "white",
                              color: "#475569", fontWeight: 600, fontSize: "0.78rem",
                              cursor: "pointer", whiteSpace: "nowrap",
                              boxShadow: "0 2px 0 #CBD5E1",
                              transition: "all 0.15s",
                            }}
                          >
                            <i className="fa-solid fa-link"/>Compartir
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setEditando(s)}
                          data-testid={`btn-editar-ciudadano-${s.folio}`}
                          title="Editar nombre / teléfono / comentarios"
                          style={{
                            display: "inline-flex", alignItems: "center", gap: "0.3rem",
                            padding: "11px 10px", borderRadius: 9,
                            border: "1.5px solid #CBD5E1", background: "white",
                            color: "#475569", fontWeight: 600, fontSize: "0.78rem",
                            cursor: "pointer", whiteSpace: "nowrap",
                            boxShadow: "0 2px 0 #CBD5E1",
                            transition: "all 0.15s",
                          }}
                        >
                          <i className="fa-solid fa-pen-to-square"/>Editar
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditando(s)}
                        data-testid={`btn-editar-ciudadano-${s.folio}`}
                        title="Editar nombre / teléfono / comentarios"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: "0.3rem",
                          padding: "11px 10px", borderRadius: 9,
                          border: "1.5px solid #CBD5E1", background: "white",
                          color: "#475569", fontWeight: 600, fontSize: "0.78rem",
                          cursor: "pointer", whiteSpace: "nowrap",
                          boxShadow: "0 2px 0 #CBD5E1",
                          transition: "all 0.15s",
                        }}
                      >
                        <i className="fa-solid fa-pen-to-square"/>Editar
                      </button>
                    )}

                    {/* ── 3. Separador + Declinar / Eliminar ── */}
                    {(onDeclinar || onEliminar) && (
                      <div className="flex items-center gap-1" style={{ borderLeft: "1px solid #E2E8F0", paddingLeft: "0.5rem", marginLeft: "0.25rem" }}>
                        {onDeclinar && s.estatus !== "DECLINADO" && (
                          <button
                            type="button"
                            onClick={() => onDeclinar(s)}
                            data-testid={`btn-declinar-${s.folio}`}
                            title={`Declinar ${s.folio}`}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: "0.3rem",
                              padding: "11px 10px", borderRadius: 9,
                              border: "1.5px solid #FCD34D", background: "white",
                              color: "#92400E", fontWeight: 600, fontSize: "0.78rem",
                              cursor: "pointer", whiteSpace: "nowrap",
                              boxShadow: "0 2px 0 #FCD34D",
                              transition: "all 0.15s",
                            }}
                          >
                            <i className="fa-solid fa-ban"/>Declinar
                          </button>
                        )}
                        {onEliminar && (
                          <button
                            type="button"
                            onClick={() => onEliminar(s)}
                            data-testid={`btn-eliminar-${s.folio}`}
                            title={`Eliminar ${s.folio}`}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: "0.3rem",
                              padding: "11px 10px", borderRadius: 9,
                              border: "1.5px solid #FCA5A5", background: "white",
                              color: "#B91C1C", fontWeight: 600, fontSize: "0.78rem",
                              cursor: "pointer", whiteSpace: "nowrap",
                              boxShadow: "0 2px 0 #FCA5A5",
                              transition: "all 0.15s",
                            }}
                          >
                            <i className="fa-solid fa-trash-can"/>Eliminar
                          </button>
                        )}
                      </div>
                    )}

                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

// ─── TAB: NUEVO REPORTE MANUAL ──────────────────────────────
function FormManual({ onCreado }) {
  const [origen, setOrigen] = useState("Oficio");
  const [folio, setFolio] = useState("");
  const [folioRiPreview, setFolioRiPreview] = useState("");
  const [nombreLugar, setNombreLugar] = useState("");
  const [direccion, setDireccion] = useState("");
  const [tipoAgua, setTipoAgua] = useState("Tratada");
  const [documento, setDocumento] = useState(null);
  const [ciudadanoNombre, setCiudadanoNombre] = useState("");
  const [ciudadanoTel, setCiudadanoTel] = useState("");
  const [comentarios, setComentarios] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [zona, setZona] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Fetch next RI folio preview when origen switches to Reporte Interno
  useEffect(() => {
    if (origen !== "Reporte Interno") return;
    axios.get(`${API}/capturista/siguiente-folio-ri`)
      .then(r => setFolioRiPreview(r.data.siguiente_folio))
      .catch(() => setFolioRiPreview("RI-????"));
  }, [origen]);

  const sufijo = documento?.name?.split(".").pop() || "pdf";
  const nombreRenamed = folio ? `OFICIO_${folio}.${sufijo}` : "OFICIO_____.pdf";

  const submit = async (e) => {
    e.preventDefault();
    if (origen === "Oficio" && !documento) {
      toast.error("Para oficios, el documento adjunto es obligatorio");
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("origen", origen);
      if (origen !== "Reporte Interno") form.append("folio", folio);
      if (nombreLugar.trim()) form.append("nombre_lugar", nombreLugar.trim());
      form.append("direccion_texto", direccion);
      form.append("tipo_agua", tipoAgua);
      if (ciudadanoNombre) form.append("ciudadano_nombre", ciudadanoNombre);
      if (ciudadanoTel) form.append("ciudadano_telefono", ciudadanoTel);
      if (comentarios) form.append("comentarios", comentarios);
      if (observaciones) form.append("observaciones", observaciones);
      if (zona) form.append("zona_sugerida", zona);
      if (documento) form.append("documento", documento, nombreRenamed);
      await axios.post(`${API}/capturista/solicitud`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onCreado();
    } catch (err) {
      toast.error("Error: " + (err.response?.data?.detail || err.message));
    } finally { setSubmitting(false); }
  };

  return (
    <section className="card-base" data-testid="form-manual">
      <div className="text-lg font-bold mb-1">Capturar Nueva Solicitud</div>
      <div className="text-xs text-slate-500 mb-5">Los campos con * son obligatorios. Los oficios requieren documento.</div>

      <form className="grid grid-cols-1 lg:grid-cols-2 gap-5" onSubmit={submit}>
        <div className="field">
          <label>Origen *</label>
          <select value={origen} onChange={(e) => setOrigen(e.target.value)} data-testid="sel-origen">
            <option>Ordinario</option>
            <option>CRC</option>
            <option>Oficio</option>
            <option>Reporte Interno</option>
          </select>
        </div>

        <div className="field">
          <label>
            Folio / Número
            {origen !== "Reporte Interno" && <span className="text-rose-600 ml-1">*</span>}
          </label>
          {origen === "Reporte Interno" ? (
            <div className="flex items-center gap-2">
              <input
                value={folioRiPreview}
                readOnly
                className="bg-slate-100 text-slate-500 cursor-not-allowed"
                data-testid="inp-folio-ri-auto"
              />
              <span className="text-xs text-slate-400 whitespace-nowrap">Auto-generado</span>
            </div>
          ) : (
            <input value={folio} onChange={(e) => setFolio(e.target.value)} placeholder="CRC677034 / OF-001" required data-testid="inp-folio"/>
          )}
        </div>

        <div className="field lg:col-span-2">
          <label>Nombre del lugar <span className="text-slate-400 font-normal">(parque, oficina, espacio público — opcional)</span></label>
          <input value={nombreLugar} onChange={(e) => setNombreLugar(e.target.value)} placeholder="Parque Las Américas / Junta Seccional 12 / Camellón Av. Tecnológico" data-testid="inp-nombre-lugar"/>
          <div className="text-xs text-slate-400 mt-1">
            <i className="fa-solid fa-map-pin"/> Se usará para buscar en el catálogo de espacios registrados y mejorar la asignación automática.
          </div>
        </div>

        <div className="field lg:col-span-2">
          <label>Lugar de Entrega / Dirección *</label>
          <input value={direccion} onChange={(e) => setDireccion(e.target.value)} placeholder="Camellón Av. Cafetales esq. Isabel Gutiérrez" required data-testid="inp-direccion"/>
          <div className="text-xs text-slate-400 mt-1">
            <i className="fa-solid fa-info-circle"/> Geocodificación automática con <strong>Google Maps</strong>. Si no se encuentra la dirección con precisión, la solicitud pasa a "Dudosos IA" para revisión manual.
          </div>
        </div>

        <div className="field">
          <label>Tipo de agua *</label>
          <div className="flex gap-2">
            {["Tratada","Potable"].map(t => (
              <button key={t} type="button" onClick={() => setTipoAgua(t)}
                      className={`btn-seleccion flex-1 ${tipoAgua===t ? (t==="Tratada"?"activo-verde":"activo") : ""}`}
                      data-testid={`btn-agua-${t.toLowerCase()}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label>Zona sugerida (opcional)</label>
          <select value={zona} onChange={(e) => setZona(e.target.value)} data-testid="sel-zona">
            <option value="">— Auto-asignar por GPS —</option>
            {ZONAS.map(z => <option key={z} value={z}>{z}</option>)}
          </select>
        </div>

        <div className="field lg:col-span-2">
          <label>Adjuntar documento {origen === "Oficio" && <span className="text-rose-600">*</span>}</label>
          <div className="upload-zone">
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setDocumento(e.target.files?.[0] || null)} data-testid="inp-archivo"/>
            <i className="fa-solid fa-cloud-arrow-up text-2xl text-slate-400 mb-2"/>
            <div className="text-sm font-semibold">{documento ? documento.name : "Click para subir PDF, JPG o PNG"}</div>
            {documento && (
              <div className="text-xs text-emerald-700 mt-2">
                <i className="fa-solid fa-rotate-right"/> Se renombrará a: <strong className="mono">{nombreRenamed}</strong>
              </div>
            )}
            <div className="text-xs text-amber-700 mt-1">
              ⚠ <strong>MOCKED</strong>: solo se guarda el nombre. Subida real a Cloudinary pendiente de keys.
            </div>
          </div>
        </div>

        <div className="field">
          <label>Nombre del ciudadano</label>
          <input value={ciudadanoNombre} onChange={(e) => setCiudadanoNombre(e.target.value)} placeholder="Juan Pérez García" data-testid="inp-ciudadano-nombre"/>
        </div>

        <div className="field">
          <label>Teléfono del ciudadano</label>
          <input value={ciudadanoTel} onChange={(e) => setCiudadanoTel(e.target.value)} placeholder="614 123 4567" data-testid="inp-ciudadano-tel"/>
        </div>

        <div className="field lg:col-span-2">
          <label>Comentarios / Descripción de la solicitud</label>
          <textarea value={comentarios} onChange={(e) => setComentarios(e.target.value)} rows={2} placeholder="Descripción de la solicitud tal como viene en el CRC / oficio…" data-testid="inp-comentarios" style={{resize:"vertical"}}/>
        </div>

        <div className="field lg:col-span-2">
          <label>Observaciones internas</label>
          <input value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Notas internas, prioridad…" data-testid="inp-obs"/>
        </div>

        <div className="lg:col-span-2 flex gap-3">
          <button type="submit" className="btn-primary flex-1" disabled={submitting} data-testid="btn-guardar-manual">
            {submitting ? "Guardando…" : "GUARDAR Y ASIGNAR A SUPERVISOR"}
            <i className="fa-solid fa-arrow-right"/>
          </button>
        </div>
      </form>
    </section>
  );
}

// ─── TAB: IMPORTACIÓN EXCEL ─────────────────────────────────
export function ImportExcelTab({ onImported, stats }) {
  const [archivo, setArchivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setArchivo(f);
    setPreview(null);
  };

  const subirParaPreview = async () => {
    if (!archivo) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", archivo);
      form.append("dry_run", "true");
      const { data } = await axios.post(`${API}/capturista/import-excel`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error("Error al leer el Excel: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const confirmarImportacion = async () => {
    if (!archivo) return;
    setConfirmando(true);
    try {
      const form = new FormData();
      form.append("file", archivo);
      form.append("dry_run", "false");
      const { data } = await axios.post(`${API}/capturista/import-excel`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const otros = data.otros_dep_omitidos || 0;
      toast.success(
        `${data.importados} registros de pipas importados · ` +
        `${otros} de otros departamentos ignorados · ` +
        `${data.duplicados_omitidos} duplicados omitidos`
      );
      setPreview(null);
      setArchivo(null);
      onImported?.();
    } catch (e) {
      toast.error("Error al importar: " + (e.response?.data?.detail || e.message));
    } finally { setConfirmando(false); }
  };

  const cancelar = () => { setArchivo(null); setPreview(null); };

  return (
    <section className="card-base" data-testid="tab-import">
      <div className="text-lg font-bold mb-2">Importación Masiva — Atención Ciudadana (CRC)</div>
      <p className="text-sm text-slate-600 mb-5">
        Sube el archivo Excel descargado del portal de Atención Ciudadana del municipio.
        El sistema parsea columnas <strong>A-J</strong> (Folio · Ubicación · Título · Dirección · Categoría),
        calcula <strong>zona automáticamente</strong> usando el catálogo de parques (vecino más cercano)
        y omite duplicados por folio.
      </p>

      {!archivo && (
        <div className="upload-zone-big" data-testid="dropzone-excel">
          <i className="fa-solid fa-file-excel text-5xl text-emerald-600 mb-3"/>
          <div className="text-lg font-bold">Sube el archivo .xlsx del portal CRC</div>
          <div className="text-sm text-slate-500 mt-2">
            Formato esperado: hoja única con encabezados en fila 1 (Fecha · Folio · Nombre · Teléfono · Ubicación · Título · Colonia · Dirección · ZONAS · Categoría)
          </div>
          <label className="btn-primary mt-5" style={{ maxWidth: 320, margin: "1.25rem auto 0", cursor: "pointer" }} data-testid="label-pick-excel">
            <i className="fa-solid fa-upload"/> SUBIR ARCHIVO
            <input type="file" accept=".xlsx,.xls" hidden onChange={onPickFile} data-testid="input-file-excel"/>
          </label>
        </div>
      )}

      {archivo && !preview && (
        <div className="rounded-lg border border-slate-200 p-4" data-testid="archivo-picked">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="text-sm">
              <i className="fa-solid fa-file-excel text-emerald-600 mr-1"/>
              <strong>{archivo.name}</strong> · {(archivo.size / 1024).toFixed(1)} KB
            </div>
            <div className="flex gap-2">
              <button className="btn-ghost" onClick={cancelar} data-testid="btn-cancel-pick">Cambiar archivo</button>
              <button className="btn-primary" onClick={subirParaPreview} disabled={loading} data-testid="btn-previsualizar-excel">
                {loading ? "Procesando…" : (<><i className="fa-solid fa-eye mr-1"/>Previsualizar</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-4" data-testid="preview-excel">
          <div className="text-sm font-bold text-slate-900 mb-2">
            <i className="fa-solid fa-circle-check text-emerald-600 mr-1"/>
            Se detectaron <strong>{preview.total_filas}</strong> reportes de pipas en el Excel
            {preview.otros_dep_omitidos > 0 && (
              <span className="text-xs font-normal text-slate-500">
                {" "}· {preview.otros_dep_omitidos} de otros departamentos ignoradas
              </span>
            )}
          </div>

          {preview.filtro_pipas_activo && preview.otros_dep_omitidos > 0 && (
            <div className="rounded-md bg-sky-50 border border-sky-200 p-2 mb-3 text-xs text-sky-900" data-testid="prev-filtro-pipas">
              <i className="fa-solid fa-filter mr-1"/>
              <strong>Filtro automático aplicado:</strong> se conservaron solo las filas cuya columna
              "Zona" contiene <span className="font-mono bg-white px-1 rounded">PIPA</span>.
              {" "}<strong>{preview.otros_dep_omitidos}</strong> registros de otros departamentos
              fueron ignorados silenciosamente.
              {preview.otros_dep_ejemplos?.length > 0 && (
                <span className="text-slate-600">
                  {" "}Ej.: {preview.otros_dep_ejemplos.slice(0, 5).map((v, i) => (
                    <span key={i} className="font-mono bg-white px-1 rounded mr-1">{v}</span>
                  ))}
                </span>
              )}
            </div>
          )}

          {/* Mapeo de columnas detectadas (transparencia para Yanett) */}
          {preview.mapeo_columnas && Object.keys(preview.mapeo_columnas).length > 0 && (
            <details className="text-xs bg-white border border-slate-200 rounded-md p-2 mb-3" data-testid="prev-mapeo">
              <summary className="cursor-pointer font-semibold text-slate-700">
                <i className="fa-solid fa-columns mr-1"/>Columnas detectadas automáticamente · {Object.keys(preview.mapeo_columnas).length} mapeadas
              </summary>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                {Object.entries(preview.mapeo_columnas).map(([campo, header]) => (
                  <div key={campo} className="flex justify-between gap-2 py-0.5">
                    <span className="text-slate-500 capitalize">{campo}:</span>
                    <span className="font-mono text-emerald-700 truncate">{header}</span>
                  </div>
                ))}
              </div>
              {preview.headers_detectados?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-100 text-slate-500">
                  Otros encabezados (ignorados): {preview.headers_detectados
                    .filter(h => !Object.values(preview.mapeo_columnas).includes(h))
                    .join(", ") || "ninguno"}
                </div>
              )}
            </details>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs my-3">
            <div className="rounded-md bg-emerald-100 text-emerald-900 p-2 text-center" data-testid="prev-importados">
              <div className="text-2xl font-bold">{preview.importados}</div>De pipas (a importar)
            </div>
            <div className="rounded-md bg-slate-100 text-slate-700 p-2 text-center" data-testid="prev-duplicados">
              <div className="text-2xl font-bold">{preview.duplicados_omitidos}</div>Duplicados
            </div>
            <div className="rounded-md bg-sky-100 text-sky-900 p-2 text-center" data-testid="prev-otros-dep">
              <div className="text-2xl font-bold">{preview.otros_dep_omitidos || 0}</div>Otros deptos. ignorados
            </div>
            <div className="rounded-md bg-amber-100 text-amber-900 p-2 text-center" data-testid="prev-sin-gps">
              <div className="text-2xl font-bold">{preview.sin_gps}</div>Sin GPS
            </div>
            <div className="rounded-md bg-rose-100 text-rose-900 p-2 text-center" data-testid="prev-dudosos">
              <div className="text-2xl font-bold">{preview.dudosos_gps + preview.dudosos_zona}</div>Dudosos
            </div>
          </div>

          {preview.errores_globales?.length > 0 && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-2 my-3 text-xs text-amber-900" data-testid="prev-warnings">
              <strong>Avisos:</strong>
              <ul className="list-disc list-inside mt-1">
                {preview.errores_globales.map((er, i) => <li key={i}>{er}</li>)}
              </ul>
            </div>
          )}

          <div className="overflow-x-auto -mx-2 mt-2">
            <table className="w-full text-xs" data-testid="prev-tabla">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-1.5">Folio</th>
                  <th className="text-left p-1.5">Dirección</th>
                  <th className="text-center p-1.5">GPS</th>
                  <th className="text-center p-1.5">Zona</th>
                  <th className="text-center p-1.5">Estado</th>
                </tr>
              </thead>
              <tbody>
                {preview.detalle.slice(0, 50).map((d, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`prev-row-${d.folio || i}`}>
                    <td className="p-1.5 mono">{d.folio || "—"}</td>
                    <td className="p-1.5 truncate max-w-[200px]">{d.direccion || "—"}</td>
                    <td className="p-1.5 text-center">
                      {d.gps_estado === "OK" ? <span className="text-emerald-700">✓</span>
                        : d.gps_estado === "SIN_GPS" ? <span className="text-amber-600">SIN</span>
                        : d.gps_estado === "DUDOSO_GPS" ? <span className="text-rose-600">DUDA</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="p-1.5 text-center mono">{d.zona_calculada || "—"}</td>
                    <td className="p-1.5 text-center">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                            style={{
                              background: d.estado === "PENDIENTE_SUPERVISOR" ? "#FEF3C7"
                                : d.estado === "DUPLICADO" ? "#F1F5F9"
                                : d.estado === "DUDOSO_ZONA" ? "#FED7AA"
                                : d.estado === "SIN_GPS" || d.estado === "DUDOSO_GPS" ? "#FEE2E2"
                                : "#E0E7FF",
                              color: d.estado === "PENDIENTE_SUPERVISOR" ? "#92400E"
                                : d.estado === "DUPLICADO" ? "#475569"
                                : d.estado === "DUDOSO_ZONA" ? "#9A3412"
                                : d.estado === "SIN_GPS" || d.estado === "DUDOSO_GPS" ? "#991B1B"
                                : "#3730A3",
                            }}>
                        {d.estado}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.detalle.length > 50 && (
              <div className="text-xs text-slate-500 p-2 text-center">… {preview.detalle.length - 50} filas más</div>
            )}
          </div>

          <div className="flex gap-2 mt-4 justify-end">
            <button className="btn-ghost" onClick={cancelar} disabled={confirmando} data-testid="btn-cancelar-preview-excel">Cancelar</button>
            <button className="btn-primary" onClick={confirmarImportacion} disabled={confirmando || preview.importados === 0} data-testid="btn-confirmar-import-excel">
              {confirmando ? "Importando…" : (<><i className="fa-solid fa-check mr-1"/>Confirmar e importar {preview.importados}</>)}
            </button>
          </div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <Mini label="Total 24h"      val={stats.total_24h}  color="var(--accent)" icon="fa-list"/>
          <Mini label="Pendientes"     val={stats.pendiente} color="#92400E" icon="fa-hourglass-half"/>
          <Mini label="Dudosos IA"     val={stats.dudoso}    color="#991B1B" icon="fa-triangle-exclamation"/>
          <Mini label="Atendidos"      val={stats.atendido}  color="var(--green)" icon="fa-check"/>
        </div>
      )}
    </section>
  );
}

// ─── TAB: HISTORIAL ────────────────────────────────────────
const RANGOS_HORAS = [
  { label: "Últimas 24h", horas: 24 },
  { label: "Últimas 72h", horas: 72 },
  { label: "7 días",      horas: 168 },
  { label: "30 días",     horas: 720 },
];

// Estatus labels para solicitudes-v2 (fuente combinada)
const ESTATUS_V2 = {
  ATENDIDO:        { lbl: "Atendido",      bg: "#D1FAE5", color: "#047857" },
  NO_PUDO_ATENDER: { lbl: "No pudo",       bg: "#FEE2E2", color: "#991B1B" },
  ASIGNADA:        { lbl: "Asignada",      bg: "#DBEAFE", color: "#1E40AF" },
  EN_RUTA:         { lbl: "En ruta",       bg: "#DBEAFE", color: "#1E40AF" },
  PENDIENTE_SUPERVISOR: { lbl: "Pendiente", bg: "#FEF3C7", color: "#92400E" },
  CANCELADO:       { lbl: "Cancelado",     bg: "#F1F5F9", color: "#475569" },
};

/** Helpers de auth para los nuevos endpoints */
function _authHeaders() {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Panel de filtros avanzados + botones Excel/PDF — se agrega dentro de HistorialTab */
function FiltrosAvanzadosPanel({ onResultados, onLimpiar }) {
  const [abierto, setAbierto]     = useState(false);
  const [opciones, setOpciones]   = useState({ origenes: [], categorias: [], pipas: [] });
  const [cargandoOpc, setCargOpc] = useState(false);

  // Filtros
  const [fechaDesde, setFechaDesde]     = useState("");
  const [fechaHasta, setFechaHasta]     = useState("");
  const [origen, setOrigen]             = useState("");
  const [categoria, setCategoria]       = useState("");
  const [estatus, setEstatus]           = useState("");
  const [pipa, setPipa]                 = useState("");

  // Estados de descarga
  const [aplicando, setAplicando]       = useState(false);
  const [descExcel, setDescExcel]       = useState(false);
  const [descPdf, setDescPdf]           = useState(false);
  const [totalReal, setTotalReal]       = useState(null); // para aviso PDF >100

  // Cargar opciones al abrir por primera vez
  useEffect(() => {
    if (!abierto || opciones.origenes.length > 0) return;
    setCargOpc(true);
    axios.get(`${API}/capturista/filtros-opciones`, { headers: _authHeaders() })
      .then(r => setOpciones(r.data || { origenes: [], categorias: [], pipas: [] }))
      .catch(() => toast.error("No se pudieron cargar los filtros"))
      .finally(() => setCargOpc(false));
  }, [abierto]); // eslint-disable-line react-hooks/exhaustive-deps

  const _params = () => {
    const p = {};
    if (fechaDesde) p.fecha_desde = fechaDesde;
    if (fechaHasta) p.fecha_hasta = fechaHasta;
    if (origen)     p.origen      = origen;
    if (categoria)  p.categoria_lugar = categoria;
    if (estatus)    p.estatus     = estatus;
    if (pipa)       p.pipa_id     = pipa;
    return p;
  };

  const hayFiltros = !!(fechaDesde || fechaHasta || origen || categoria || estatus || pipa);

  const aplicar = () => {
    setAplicando(true);
    axios.get(`${API}/capturista/solicitudes-v2`, {
      headers: _authHeaders(),
      params: { ..._params(), limit: 500 },
    })
      .then(r => {
        const rows = r.data || [];
        setTotalReal(rows.length);
        onResultados(rows);
      })
      .catch(() => toast.error("Error al aplicar filtros"))
      .finally(() => setAplicando(false));
  };

  const limpiar = () => {
    setFechaDesde(""); setFechaHasta(""); setOrigen("");
    setCategoria(""); setEstatus(""); setPipa("");
    setTotalReal(null);
    onLimpiar();
  };

  const descargarExcel = () => {
    setDescExcel(true);
    const params = new URLSearchParams(_params());
    params.set("limit", "2000");
    // Crea URL temporal con token en header no es posible con window.open;
    // usamos fetch+blob para respetar el JWT
    axios.get(`${API}/capturista/solicitudes-excel-v2`, {
      headers: _authHeaders(),
      params: _params(),
      responseType: "blob",
    }).then(r => {
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DMU614_intervenciones.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error("Error al generar Excel"))
      .finally(() => setDescExcel(false));
  };

  const descargarPdf = async () => {
    setDescPdf(true);
    axios.get(`${API}/capturista/solicitudes-pdf-totales`, {
      headers: _authHeaders(),
      params: _params(),
      responseType: "blob",
    }).then(r => {
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DMU614_totales.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error("Error al generar PDF"))
      .finally(() => setDescPdf(false));
  };

  const inputCls = "border border-slate-300 rounded px-2 py-1.5 text-sm bg-white w-full focus:outline-none focus:ring-1 focus:ring-blue-400";
  const selectCls = inputCls + " cursor-pointer";

  return (
    <div className="mb-4" data-testid="panel-filtros-avanzados">
      {/* Botón toggle */}
      <button
        onClick={() => setAbierto(v => !v)}
        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded border"
        style={{ background: abierto ? "#EFF6FF" : "white", borderColor: "#93C5FD", color: "#1D4ED8" }}
        data-testid="btn-toggle-filtros"
      >
        <i className={`fa-solid fa-sliders`}/>
        Filtros avanzados
        <i className={`fa-solid ${abierto ? "fa-chevron-up" : "fa-chevron-down"} ml-0.5`}/>
        {hayFiltros && <span className="ml-1 bg-blue-600 text-white rounded-full px-1.5 py-0.5" style={{ fontSize: "0.65rem" }}>●</span>}
      </button>

      {/* Panel colapsable */}
      {abierto && (
        <div
          className="mt-2 border border-slate-200 rounded-lg p-4 bg-slate-50"
          data-testid="filtros-panel-body"
        >
          {cargandoOpc ? (
            <div className="text-sm text-slate-500 py-2"><i className="fa-solid fa-spinner fa-spin mr-2"/>Cargando opciones…</div>
          ) : (
            <>
              {/* Fila 1: fechas */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha desde</label>
                  <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} className={inputCls}/>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha hasta</label>
                  <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} className={inputCls}/>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Origen</label>
                  <select
                    value={origen}
                    onChange={e => {
                      const v = e.target.value;
                      setOrigen(v);
                      // Apoyo a Bomberos fija categoría automáticamente
                      if (v === "Apoyo a Bomberos") setCategoria("bomberos");
                      else if (v === "Taller") setCategoria("");
                      else if (origen === "Apoyo a Bomberos" || origen === "Taller") setCategoria("");
                    }}
                    className={selectCls}
                  >
                    <option value="">Todos</option>
                    {opciones.origenes.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Categoría</label>
                  {origen === "Apoyo a Bomberos" ? (
                    <select value="bomberos" disabled className={selectCls} style={{ opacity: 0.7, cursor: "not-allowed" }}>
                      <option value="bomberos">Apoyo a Bomberos</option>
                    </select>
                  ) : origen === "Taller" ? (
                    <select value="taller" disabled className={selectCls} style={{ opacity: 0.7, cursor: "not-allowed" }}>
                      <option value="taller">Taller</option>
                    </select>
                  ) : (
                    <select value={categoria} onChange={e => setCategoria(e.target.value)} className={selectCls}>
                      <option value="">Todas</option>
                      {opciones.categorias.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                </div>
              </div>

              {/* Fila 2: estatus + pipa */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Estatus</label>
                  <select value={estatus} onChange={e => setEstatus(e.target.value)} className={selectCls}>
                    <option value="">Todos</option>
                    <option value="ATENDIDO">Atendido</option>
                    <option value="NO_PUDO_ATENDER">No pudo atender</option>
                    <option value="abierto">Abierto / En proceso</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Pipa</label>
                  <select value={pipa} onChange={e => setPipa(e.target.value)} className={selectCls}>
                    <option value="">Todas</option>
                    {opciones.pipas.map(p => {
                      // label es "309 — Potable" o solo "309"; value debe ser el ID
                      const pid = p.split(" —")[0].trim();
                      return <option key={pid} value={pid}>{p}</option>;
                    })}
                  </select>
                </div>
              </div>

              {/* Botones de acción */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={aplicar}
                  disabled={aplicando}
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded"
                  style={{ background: "#1D4ED8", color: "white", opacity: aplicando ? 0.6 : 1 }}
                  data-testid="btn-aplicar-filtros"
                >
                  <i className={`fa-solid ${aplicando ? "fa-spinner fa-spin" : "fa-magnifying-glass"}`}/>
                  {aplicando ? "Buscando…" : "Aplicar filtros"}
                </button>

                {hayFiltros && (
                  <button
                    onClick={limpiar}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded border border-slate-300 text-slate-600 hover:bg-white"
                    data-testid="btn-limpiar-filtros"
                  >
                    <i className="fa-solid fa-xmark"/> Limpiar
                  </button>
                )}

                <div className="ml-auto flex gap-2">
                  <button
                    onClick={descargarExcel}
                    disabled={descExcel}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded border"
                    style={{ background: "#16A34A", color: "white", borderColor: "#15803D", opacity: descExcel ? 0.6 : 1 }}
                    data-testid="btn-excel-avanzado"
                  >
                    <i className={`fa-solid ${descExcel ? "fa-spinner fa-spin" : "fa-file-excel"}`}/>
                    {descExcel ? "Generando…" : "Excel avanzado"}
                  </button>

                  <button
                    onClick={descargarPdf}
                    disabled={descPdf}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded border"
                    style={{ background: "#0D2B52", color: "white", borderColor: "#1E3A5F", opacity: descPdf ? 0.6 : 1 }}
                    data-testid="btn-pdf-ejecutivo"
                  >
                    <i className={`fa-solid ${descPdf ? "fa-spinner fa-spin" : "fa-file-pdf"}`}/>
                    {descPdf ? "Generando PDF…" : "PDF de totales"}
                  </button>
                </div>
              </div>

              {totalReal !== null && (
                <div className="mt-3 text-xs text-slate-500">
                  <i className="fa-solid fa-circle-info mr-1"/>
                  {totalReal === 0
                    ? "Sin resultados para los filtros seleccionados."
                    : `${totalReal} registros encontrados${totalReal > 100 ? ` — el PDF mostrará los primeros 100` : ""}.`}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Tarjeta de un registro de solicitudes-v2 (fuente combinada CRC + eventos) */
function TarjetaV2({ r, onVerFoto }) {
  const est = ESTATUS_V2[r.estatus] || { lbl: r.estatus || "—", bg: "#F1F5F9", color: "#475569" };
  const fotos = Array.isArray(r.fotos) ? r.fotos.filter(Boolean) : [];
  const tieneComprobante = r.folio && (r.estatus === "ATENDIDO" || r.estatus === "NO_PUDO_ATENDER");
  const base = process.env.REACT_APP_PUBLIC_URL || "https://dmu614.com";
  const linkComprobante = tieneComprobante ? `${base}/evidencia/${encodeURIComponent(r.folio)}` : null;

  return (
    <li className="border border-slate-200 rounded-lg p-2.5 bg-white" data-testid={`v2-${r.folio || r.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {r.folio && <span className="mono text-xs font-bold text-slate-700">{r.folio}</span>}
          {r._fuente === "evento" && !r.folio && (
            <span className="text-xs text-slate-400 italic">Actividad chofer</span>
          )}
          <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: est.bg, color: est.color }}>
            {est.lbl}
          </span>
          {r.origen_display && (
            <span className="text-xs px-2 py-0.5 rounded" style={{ background: "#F1F5F9", color: "#475569" }}>
              {r.origen_display}
            </span>
          )}
          {r.categoria_lugar && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: "#EFF6FF", color: "#1D4ED8" }}>
              <i className="fa-solid fa-tag mr-1"/>{r.categoria_lugar}
            </span>
          )}
        </div>
        <span className="text-xs text-slate-400">{r.fecha || ""}</span>
      </div>

      {r.lugar && (
        <div className="text-xs font-semibold text-slate-600 mt-1">
          <i className="fa-solid fa-map-pin mr-1 text-slate-400"/>{r.lugar}
          {r.colonia ? ` · ${r.colonia}` : ""}
        </div>
      )}
      {r.direccion && r.direccion !== r.lugar && (
        <div className="text-xs text-slate-500 mt-0.5">{r.direccion}</div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-1 text-xs text-slate-500">
        {r.chofer  && <span><i className="fa-solid fa-user mr-1"/>{r.chofer}</span>}
        {r.pipa    && <span><i className="fa-solid fa-truck-droplet mr-1"/>Pipa {r.pipa}</span>}
        {r.companero && <span><i className="fa-solid fa-user-group mr-1"/>{r.companero}</span>}
      </div>

      {r.comentario && (
        <div className="text-xs text-slate-600 mt-1 italic">
          <i className="fa-solid fa-comment mr-1 text-slate-400"/>{r.comentario}
        </div>
      )}
      {r.motivo_no_pudo && (
        <div className="text-xs text-red-700 mt-1">
          <i className="fa-solid fa-circle-xmark mr-1"/>Motivo: {r.motivo_no_pudo}
        </div>
      )}

      {fotos.length > 0 && (
        <div className="flex gap-1.5 mt-2 flex-wrap">
          {fotos.map((url, i) => (
            <div key={i} className="relative group" style={{ width: 56, height: 56 }}>
              <img
                src={url} alt="evidencia"
                className="w-full h-full object-cover rounded border border-slate-200 cursor-pointer"
                onClick={() => onVerFoto && onVerFoto(url)}
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded opacity-0 group-hover:opacity-100 transition">
                <i className="fa-solid fa-magnifying-glass-plus text-white text-xs"/>
              </div>
            </div>
          ))}
        </div>
      )}

      {linkComprobante && (
        <button
          className="mt-2 text-xs inline-flex items-center gap-1 bg-white border border-slate-300 text-slate-700 rounded px-2 py-0.5 hover:bg-slate-50"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(linkComprobante);
              toast.success("Link de evidencia copiado.");
            } catch {
              toast.error("No se pudo copiar: " + linkComprobante);
            }
          }}
        >
          <i className="fa-solid fa-link"/> Copiar link evidencia
        </button>
      )}
    </li>
  );
}

function HistorialTab() {
  const [horas, setHoras]         = useState(24);
  const [solicitudes, setSols]    = useState([]);
  const [cargando, setCargando]   = useState(false);
  const [verDoc, setVerDoc]       = useState(null);
  const [descargando, setDesc]    = useState(false);

  // Resultados del panel avanzado (null = no hay filtros activos)
  const [resultadosV2, setResultadosV2] = useState(null);

  useEffect(() => {
    setCargando(true);
    axios.get(`${API}/capturista/solicitudes`, { params: { horas } })
      .then(r => setSols(r.data || []))
      .catch(() => toast.error("Error al cargar historial"))
      .finally(() => setCargando(false));
  }, [horas]);

  const descargarExcel = () => {
    setDesc(true);
    const hasta = new Date();
    const desde = new Date(hasta - horas * 60 * 60 * 1000);
    const fmt = d => d.toLocaleDateString("en-CA"); // YYYY-MM-DD
    axios.get(`${API}/capturista/solicitudes-excel-v2`, {
      headers: _authHeaders(),
      params: { fecha_desde: fmt(desde), fecha_hasta: fmt(hasta), origen: "todos" },
      responseType: "blob",
    }).then(r => {
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DMU614_historial.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error("Error al descargar Excel"))
      .finally(() => setDesc(false));
  };

  return (
    <section className="card-base" data-testid="tab-historial">
      {/* ── Cabecera con selector de rango + botón Excel ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-lg font-bold">Historial y Descargas</div>
          <div className="text-sm text-slate-500">
            {resultadosV2
              ? `${resultadosV2.length} registros · filtros avanzados`
              : cargando ? "Cargando…" : `${solicitudes.length} registros · agrupados por origen`}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Selector de rango — solo visible cuando no hay filtros avanzados activos */}
          {!resultadosV2 && (
            <div className="flex gap-1.5 flex-wrap">
              {RANGOS_HORAS.map(r => (
                <button
                  key={r.horas}
                  onClick={() => setHoras(r.horas)}
                  className={`btn-seleccion ${horas === r.horas ? "activo" : ""}`}
                  style={{ padding: "0.45rem 0.85rem", minHeight: "unset", fontSize: "0.78rem" }}
                >
                  {r.label}
                </button>
              ))}
            </div>
          )}
          {/* Botón Excel simple — solo visible cuando no hay filtros avanzados */}
          {!resultadosV2 && (
            <button
              onClick={descargarExcel}
              disabled={descargando || cargando}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded border"
              style={{
                background: "#16A34A", color: "white",
                borderColor: "#15803D",
                opacity: (descargando || cargando) ? 0.6 : 1,
                cursor: (descargando || cargando) ? "not-allowed" : "pointer",
              }}
              data-testid="btn-descargar-excel"
            >
              <i className="fa-solid fa-file-excel"/>
              {descargando ? "Descargando…" : "Descargar Excel"}
            </button>
          )}
        </div>
      </div>

      {/* ── Panel de filtros avanzados (nuevo) ── */}
      <FiltrosAvanzadosPanel
        onResultados={rows => setResultadosV2(rows)}
        onLimpiar={() => setResultadosV2(null)}
      />

      {/* ── Lista de resultados ── */}
      {resultadosV2 ? (
        /* Vista avanzada: lista plana de TarjetaV2 */
        <div data-testid="lista-v2">
          {resultadosV2.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <i className="fa-solid fa-magnifying-glass text-2xl mb-2 block"/>
              Sin resultados para los filtros seleccionados.
            </div>
          ) : (
            <ul className="space-y-2">
              {resultadosV2.map(r => (
                <TarjetaV2 key={r.id || r.folio} r={r} onVerFoto={setVerDoc}/>
              ))}
            </ul>
          )}
        </div>
      ) : (
        /* Vista clásica: agrupada por origen */
        <div data-testid="lista-clasica">
          {["Ordinario","CRC","Oficio","Reporte Interno"].map(o => {
            const lista0 = solicitudes.filter(s => s.origen === o);
            if (!lista0.length) return null;
            const lista = [...lista0].sort((a, b) => {
              const ac = a.estatus === "ATENDIDO" || a.estatus === "NO_PUDO_ATENDER";
              const bc = b.estatus === "ATENDIDO" || b.estatus === "NO_PUDO_ATENDER";
              if (ac !== bc) return ac ? -1 : 1;
              return (b.actualizado_en || "").localeCompare(a.actualizado_en || "");
            });
            return (
              <div key={o} className="mb-5">
                <div className="text-sm font-semibold text-slate-700 mb-2">
                  <span className="origen-pill mr-2" style={{ background: ORIGEN_BG[o]?.bg, color: ORIGEN_BG[o]?.color }}>{o}</span>
                  {lista.length} registros
                </div>
                <ul className="space-y-2">
                  {lista.slice(0, 50).map(s => {
                    const est    = ESTATUS[s.estatus] || { lbl: s.estatus, color: "#475569" };
                    const cerrada = s.estatus === "ATENDIDO" || s.estatus === "NO_PUDO_ATENDER";
                    const fechaCierre = s.fecha_atencion || s.fecha_no_pudo;
                    // Nombre del chofer: preferir el nuevo campo, caer en el legado
                    const choferNom = s.chofer_asignado_nombre || s.chofer_nombre || "";
                    const fotos = Array.isArray(s.fotos_atencion) ? s.fotos_atencion.filter(Boolean) : [];
                    return (
                      <li key={s.id} className="border border-slate-200 rounded-lg p-2.5" data-testid={`hist-${s.folio}`}>
                        {/* Folio + estatus */}
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="mono text-xs font-bold text-slate-700">{s.folio}</span>
                          <span className="text-xs font-bold px-2 py-0.5 rounded" data-testid={`hist-estatus-${s.folio}`}
                                style={{ background: est.bg, color: est.color }}>
                            <i className={`fa-solid ${est.icon || ""} mr-1`}/>{est.lbl}
                          </span>
                        </div>
                        {/* Nombre del lugar + dirección */}
                        {s.nombre_lugar && (
                          <div className="text-xs font-semibold text-slate-600 mt-1">
                            <i className="fa-solid fa-map-pin mr-1 text-slate-400"/>{s.nombre_lugar}
                          </div>
                        )}
                        <div className="text-sm text-slate-700 mt-0.5">{s.direccion_texto}</div>
                        {/* Chofer asignado — visible en CUALQUIER estatus si hay dato */}
                        {choferNom && !cerrada && (
                          <div className="text-xs text-slate-500 mt-1" data-testid={`hist-chofer-asig-${s.folio}`}>
                            <i className="fa-solid fa-user-check mr-1 text-blue-400"/>
                            Asignado a <strong>{choferNom}</strong>
                        {s.fecha_programada ? ` · ${s.fecha_programada}` : ""}
                      </div>
                    )}
                    {/* Detalle de cierre */}
                    {cerrada && (
                      <div className="mt-2 p-2 rounded text-xs" style={{ background: s.estatus === "ATENDIDO" ? "#F0FDF4" : "#FEF2F2" }}>
                        {choferNom && (
                          <div data-testid={`hist-chofer-${s.folio}`}>
                            <i className="fa-solid fa-user mr-1 text-slate-500"/>
                            <strong>{choferNom}</strong>
                          </div>
                        )}
                        {fechaCierre && (
                          <div className="text-slate-500 mt-0.5">
                            <i className="fa-regular fa-clock mr-1"/>
                            {new Date(fechaCierre).toLocaleString("es-MX")}
                          </div>
                        )}
                        {s.gps_atencion_lat && s.gps_atencion_lon && (
                          <div className="text-slate-500 mt-0.5 mono" data-testid={`hist-gps-${s.folio}`}>
                            <i className="fa-solid fa-location-dot mr-1"/>
                            {s.gps_atencion_lat.toFixed(5)}, {s.gps_atencion_lon.toFixed(5)}
                          </div>
                        )}
                        {s.estatus === "NO_PUDO_ATENDER" && (s.motivo_no_pudo || s.motivo_no_atendido) && (
                          <div className="mt-1 text-red-800" data-testid={`hist-motivo-${s.folio}`}>
                            <i className="fa-solid fa-comment mr-1"/>
                            <strong>Motivo:</strong> {s.motivo_no_pudo || s.motivo_no_atendido}
                          </div>
                        )}
                        {/* Thumbnails de evidencia */}
                        {fotos.length > 0 && (
                          <div className="flex gap-1.5 mt-2 flex-wrap" data-testid={`hist-fotos-${s.folio}`}>
                            {fotos.map((url, i) => (
                              <div key={i} className="relative group" style={{ width: 64, height: 64 }}>
                                <img
                                  src={url} alt="evidencia"
                                  className="w-full h-full object-cover rounded border border-slate-200 cursor-pointer"
                                  onClick={() => setVerDoc(url)}
                                  data-testid={`hist-foto-${s.folio}-${i}`}
                                />
                                {/* Botón descarga individual */}
                                <a
                                  href={url} download target="_blank" rel="noreferrer"
                                  className="absolute inset-0 flex items-center justify-center bg-black/50 text-white rounded opacity-0 group-hover:opacity-100 transition"
                                  title="Descargar foto"
                                  onClick={e => e.stopPropagation()}
                                  data-testid={`hist-dl-foto-${s.folio}-${i}`}
                                >
                                  <i className="fa-solid fa-download text-sm"/>
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Copiar link público */}
                        <button
                          className="mt-2 text-xs inline-flex items-center gap-1 bg-white border border-slate-300 text-slate-700 rounded px-2 py-1 hover:bg-slate-50"
                          onClick={async () => {
                            const url = `${window.location.origin}/evidencia/${encodeURIComponent(s.folio)}`;
                            try {
                              if (navigator.clipboard?.writeText) {
                                await navigator.clipboard.writeText(url);
                              } else {
                                const ta = document.createElement("textarea");
                                ta.value = url; document.body.appendChild(ta); ta.select();
                                document.execCommand("copy"); document.body.removeChild(ta);
                              }
                              toast.success("Link copiado. Pégalo en tu respuesta a Atención Ciudadana.");
                            } catch {
                              toast.error("No se pudo copiar. Link: " + url);
                            }
                          }}
                          data-testid={`btn-copiar-link-${s.folio}`}>
                          <i className="fa-solid fa-link"/> Copiar link de evidencia
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
        </div>
      )}

      {/* Lightbox */}
      {verDoc && (
        <div className="modal-backdrop" onClick={() => setVerDoc(null)} data-testid="modal-foto">
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <img src={verDoc} alt="evidencia ampliada" style={{ width: "100%", borderRadius: 8 }}/>
            <div className="flex gap-2 mt-2">
              <a href={verDoc} download target="_blank" rel="noreferrer"
                 className="btn-primary text-sm" style={{ padding: "0.4rem 1rem" }}>
                <i className="fa-solid fa-download mr-1"/> Descargar
              </a>
              <button className="btn-ghost" onClick={() => setVerDoc(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ─── ASIDE: ALERTAS Y DUDOSOS IA / GPS ───────────────────────
function AlertasIA({ dudosos, onCorregir, onEliminar, onDeclinar }) {
  const corregibles = new Set(["SIN_GPS", "DUDOSO_GPS", "DUDOSO_ZONA"]);
  const motivoLbl = {
    SIN_GPS: "Sin coordenadas GPS",
    DUDOSO_GPS: "GPS fuera de Chihuahua",
    DUDOSO_ZONA: "Lejos del catálogo de zonas",
    DUDOSO_IA: "Geocoding falló",
  };
  return (
    <div className="card-base mb-4" data-testid="card-dudosos">
      <div className="flex justify-between items-center mb-3">
        <div className="text-base font-bold">Alertas y Dudosos IA</div>
        <span className="bg-rose-100 text-rose-800 font-bold text-xs px-2 py-0.5 rounded-full" data-testid="dudosos-count">{dudosos.length}</span>
      </div>
      {dudosos.length === 0 && (
        <div className="text-sm text-slate-400 py-4 text-center"><i className="fa-solid fa-circle-check text-emerald-500"/> Sin pendientes de revisión</div>
      )}
      <ul className="space-y-2">
        {dudosos.slice(0, 8).map(s => {
          const puedeCorregir = corregibles.has(s.estatus);
          return (
            <li key={s.id} className="p-3 rounded-lg bg-rose-50 border border-rose-200" data-testid={`dudoso-${s.folio}`}>
              <div className="flex items-start gap-2">
                <i className="fa-solid fa-circle-exclamation text-rose-600 mt-0.5"/>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="mono text-xs font-bold text-rose-900">{s.folio}</span>
                    <span className="text-[10px] bg-rose-200 text-rose-800 px-1.5 py-0.5 rounded font-bold">{s.estatus}</span>
                  </div>
                  <div className="text-xs text-slate-700 truncate mt-0.5">{s.direccion_texto || s.titulo}</div>
                  <div className="text-[10px] text-rose-700 mt-1">{motivoLbl[s.estatus] || s.razon_dudoso}</div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {puedeCorregir && (
                      <button
                        className="text-xs font-semibold bg-white border border-rose-300 text-rose-700 rounded px-2 py-1 hover:bg-rose-100 inline-flex items-center gap-1"
                        onClick={() => onCorregir?.(s)}
                        data-testid={`btn-corregir-${s.folio}`}>
                        <i className="fa-solid fa-location-dot"/> Corregir ubicación
                      </button>
                    )}
                    {onDeclinar && (
                      <button
                        type="button"
                        className="text-xs font-semibold rounded px-2 py-1 inline-flex items-center gap-1 transition hover:bg-slate-50"
                        style={{ border: "1px solid #D1D5DB", color: "#6B7280", background: "white" }}
                        onClick={() => onDeclinar(s)}
                        title={`Declinar ${s.folio}`}
                        data-testid={`btn-declinar-alerta-${s.folio}`}>
                        <i className="fa-solid fa-ban"/> Declinar
                      </button>
                    )}
                    {onEliminar && (
                      <button
                        type="button"
                        className="text-xs font-semibold rounded px-2 py-1 inline-flex items-center gap-1 transition hover:bg-rose-50"
                        style={{ border: "1px solid #FCA5A5", color: "#B91C1C", background: "white" }}
                        onClick={() => onEliminar(s)}
                        title={`Eliminar ${s.folio}`}
                        data-testid={`btn-eliminar-alerta-${s.folio}`}>
                        <i className="fa-solid fa-trash-can"/> Eliminar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── MODAL: DECLINAR ───────────────────────────────────────
const MOTIVOS_DECLINAR = [
  "No corresponde a esta dependencia",
  "Fuera de zona de cobertura",
  "Reporte duplicado",
  "Información insuficiente / no se pudo ubicar",
  "Otro",
];

function ModalDeclinar({ solicitud, onClose, onConfirm }) {
  const [motivo, setMotivo] = useState("");
  const [comentario, setComentario] = useState("");
  const [busy, setBusy] = useState(false);

  const esOtro = motivo === "Otro";
  const valido = motivo && (!esOtro || comentario.trim());

  const confirmar = async () => {
    if (!valido) return;
    setBusy(true);
    try {
      await onConfirm(solicitud, motivo, comentario.trim() || null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="modal-declinar">
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480, background: "white", borderRadius: 14, padding: "1.25rem", width: "100%" }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-bold">
            <i className="fa-solid fa-ban text-slate-500 mr-1"/>
            Declinar / No corresponde
          </div>
          <button className="btn-ghost p-1" onClick={onClose} data-testid="btn-close-declinar">
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-4">
          <div className="text-xs text-slate-500 uppercase font-bold mb-1">Folio</div>
          <div className="mono text-sm font-bold text-slate-900">{solicitud.folio}</div>
          <div className="text-sm text-slate-600 mt-0.5">{solicitud.direccion_texto || <em>Sin dirección</em>}</div>
        </div>

        <label className="text-xs font-semibold text-slate-700 block mb-1">Motivo de declinación *</label>
        <select
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-200"
          data-testid="select-motivo-declinar"
        >
          <option value="">— Selecciona un motivo —</option>
          {MOTIVOS_DECLINAR.map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Comentario {esOtro ? "*" : "(opcional)"}
        </label>
        <textarea
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          placeholder={esOtro ? "Describe el motivo..." : "Observaciones adicionales (opcional)"}
          rows={3}
          className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          data-testid="textarea-comentario-declinar"
        />

        <div className="flex gap-2 mt-4 justify-end">
          <button className="btn-ghost" onClick={onClose} disabled={busy} data-testid="btn-cancelar-declinar">
            Cancelar
          </button>
          <button
            className="btn-primary"
            onClick={confirmar}
            disabled={busy || !valido}
            data-testid="btn-confirmar-declinar"
            style={{ background: "#6B7280", borderColor: "#6B7280" }}
          >
            {busy ? "Guardando…" : <><i className="fa-solid fa-ban mr-1"/>Declinar</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL: CERRAR CRC/OFICIO ──────────────────────────────
const MOTIVOS_NO_PUDO = [
  "No corresponde a esta dependencia",
  "Fuera de zona de cobertura",
  "Reporte duplicado",
  "Información insuficiente / no se pudo ubicar",
  "Otro",
];

function ModalCerrar({ solicitud, onClose, onCerrada }) {
  const [estatus, setEstatus]   = useState("ATENDIDO");
  const [pipa, setPipa]         = useState("");
  const [chofer, setChofer]     = useState("");
  const [motivo, setMotivo]     = useState("");
  const [fotos, setFotos]       = useState([]);   // array de dataURL base64
  const [dragOver, setDragOver] = useState(false);
  const [notas, setNotas]       = useState("");
  const [busy, setBusy]         = useState(false);

  const esAtendido = estatus === "ATENDIDO";
  const valido = esAtendido ? fotos.length > 0 : motivo.trim().length > 0;

  // Procesa File → dataURL y lo agrega al array (máx 3)
  const agregarArchivos = (files) => {
    const arr = [...files].slice(0, 3 - fotos.length);
    arr.forEach(f => {
      if (!f.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = () => setFotos(prev => [...prev, reader.result].slice(0, 3));
      reader.readAsDataURL(f);
    });
  };

  const onInputChange = (e) => { agregarArchivos(e.target.files || []); e.target.value = ""; };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    agregarArchivos(e.dataTransfer.files || []);
  };

  const confirmar = async () => {
    if (!valido) return;
    setBusy(true);
    try {
      const body = {
        estatus,
        pipa: pipa.trim() || null,
        chofer: chofer.trim() || null,
        motivo: esAtendido ? null : motivo,
        fotos_data: fotos.length > 0 ? fotos : null,
        notas: notas.trim() || null,
      };
      await axios.post(
        `${API}/capturista/solicitud/${solicitud.id}/cerrar`,
        body,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      toast.success(`Folio ${solicitud.folio} cerrado como ${esAtendido ? "ATENDIDO" : "NO PUDO ATENDER"}`);
      onCerrada(solicitud.id, estatus);
    } catch (e) {
      toast.error("Error al cerrar: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const BtnEstatus = ({ valor, label, icon, colorActivo, bgActivo, shadowActivo }) => (
    <button
      type="button"
      onClick={() => setEstatus(valor)}
      style={{
        flex: 1, minHeight: 52, padding: "0.9rem 0.85rem",
        borderRadius: 13, border: `1.5px solid ${estatus === valor ? colorActivo : "#CBD5E1"}`,
        background: estatus === valor ? bgActivo : "#FFFFFF",
        color: estatus === valor ? colorActivo : "#475569",
        fontWeight: 700, fontSize: "0.88rem", cursor: "pointer",
        transition: "all 0.15s ease",
        boxShadow: estatus === valor
          ? `0 4px 0 ${shadowActivo}, 0 6px 10px ${shadowActivo}4D`
          : "0 3px 0 #CBD5E1, 0 4px 8px rgba(0,0,0,0.06)",
        display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem",
        userSelect: "none",
      }}
    >
      <i className={`fa-solid ${icon}`}/>{label}
    </button>
  );

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="modal-cerrar">
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 960, background: "white", borderRadius: 14, padding: "1.25rem", width: "100%" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-bold">
            <i className="fa-solid fa-circle-check text-emerald-600 mr-1"/>
            Cerrar solicitud
          </div>
          <button className="btn-ghost p-1" onClick={onClose} data-testid="btn-close-cerrar">
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>

        {/* 2-column grid on md+, single column on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* ── Columna izquierda: datos del cierre ── */}
          <div className="flex flex-col gap-3">
            {/* Info del folio */}
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
              <div className="text-xs text-slate-500 uppercase font-bold mb-1">Folio</div>
              <div className="mono text-sm font-bold text-slate-900">{solicitud.folio}</div>
              <div className="text-sm text-slate-600 mt-0.5">{solicitud.direccion_texto || <em>Sin dirección</em>}</div>
            </div>

            {/* Toggle Atendido / No pudo atender */}
            <div>
              <div className="text-xs font-bold text-slate-600 uppercase mb-2">Resultado *</div>
              <div className="flex gap-2">
                <BtnEstatus valor="ATENDIDO" label="Atendido" icon="fa-check"
                  colorActivo="#065F46" bgActivo="#DCFCE7" shadowActivo="#15803D"/>
                <BtnEstatus valor="NO_PUDO_ATENDER" label="No pudo atender" icon="fa-triangle-exclamation"
                  colorActivo="#991B1B" bgActivo="#FEE2E2" shadowActivo="#DC2626"/>
              </div>
            </div>

            {/* Pipa y Chofer (texto libre, opcionales) */}
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-bold text-slate-600 uppercase">Pipa (opcional)</span>
                <input value={pipa} onChange={e => setPipa(e.target.value)}
                  placeholder="Ej. P-05"
                  className="w-full border border-slate-300 rounded px-2 py-2 mt-1 text-sm"
                  data-testid="cerrar-inp-pipa"/>
              </label>
              <label className="block">
                <span className="text-xs font-bold text-slate-600 uppercase">Chofer (opcional)</span>
                <input value={chofer} onChange={e => setChofer(e.target.value.toUpperCase())}
                  placeholder="NOMBRE CHOFER"
                  className="w-full border border-slate-300 rounded px-2 py-2 mt-1 text-sm uppercase"
                  data-testid="cerrar-inp-chofer"/>
              </label>
            </div>

            {/* Motivo (solo NO_PUDO_ATENDER) */}
            {!esAtendido && (
              <label className="block">
                <span className="text-xs font-bold text-slate-600 uppercase">Motivo *</span>
                <select value={motivo} onChange={e => setMotivo(e.target.value)}
                  className="w-full border border-slate-300 rounded px-2 py-2 mt-1 text-sm"
                  data-testid="cerrar-sel-motivo">
                  <option value="">— Selecciona —</option>
                  {MOTIVOS_NO_PUDO.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
            )}

            {/* Notas opcionales */}
            <label className="block">
              <span className="text-xs font-bold text-slate-600 uppercase">Notas (opcional)</span>
              <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={3}
                placeholder="Observaciones adicionales…"
                className="w-full border border-slate-300 rounded px-2 py-2 mt-1 text-sm"
                data-testid="cerrar-inp-notas"/>
            </label>
          </div>

          {/* ── Columna derecha: foto de evidencia ── */}
          <div className="flex flex-col gap-2">
            <div className="text-xs font-bold text-slate-600 uppercase mb-1">
              Foto de evidencia {esAtendido ? "*" : "(opcional)"}
            </div>

            {/* Miniaturas ya cargadas */}
            {fotos.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-2">
                {fotos.map((f, i) => (
                  <div key={i} className="cap-foto-mini" data-testid={`cerrar-foto-${i}`}>
                    <img src={f} alt={`foto-${i}`}/>
                    <button type="button" className="cap-foto-x"
                      onClick={() => setFotos(prev => prev.filter((_, k) => k !== i))}
                      data-testid={`cerrar-foto-quitar-${i}`}>
                      <i className="fa-solid fa-xmark"/>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Zona drag & drop — ocupa el espacio restante de la columna */}
            {fotos.length < 3 && (
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                style={{
                  border: `2px dashed ${dragOver ? "#059669" : "#CBD5E1"}`,
                  borderRadius: 10,
                  padding: "2rem 1rem",
                  background: dragOver ? "#F0FDF4" : "#F8FAFC",
                  textAlign: "center",
                  transition: "all 0.15s",
                  cursor: "pointer",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  minHeight: 220,
                }}
                data-testid="cerrar-dropzone"
              >
                <i className="fa-solid fa-cloud-arrow-up text-slate-400 text-4xl block"/>
                <div className="text-sm text-slate-500">
                  Arrastra aquí la foto de evidencia
                </div>
                <label style={{
                  display: "inline-flex", alignItems: "center", gap: "0.4rem",
                  padding: "0.55rem 1.2rem", borderRadius: 7,
                  border: "1.5px solid #CBD5E1", background: "white",
                  color: "#475569", fontWeight: 600, fontSize: "0.85rem",
                  cursor: "pointer", transition: "all 0.15s",
                  boxShadow: "0 2px 0 #CBD5E1",
                }}>
                  <i className="fa-solid fa-images"/>Elegir de la galería
                  <input type="file" accept="image/*" multiple
                    onChange={onInputChange} style={{ display: "none" }}
                    data-testid="cerrar-inp-foto"/>
                </label>
                <div className="text-xs text-slate-400">
                  JPG, PNG, HEIC · máx. 3 fotos
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer — ancho completo */}
        <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-slate-200">
          <button className="btn-ghost" onClick={onClose} disabled={busy} data-testid="btn-cancelar-cerrar">
            Cancelar
          </button>
          <button
            onClick={confirmar}
            disabled={busy || !valido}
            data-testid="btn-confirmar-cerrar"
            style={{
              background: valido && !busy ? "#059669" : "#D1FAE5",
              color: valido && !busy ? "white" : "#6EE7B7",
              border: "none", borderRadius: 8, padding: "0.5rem 1.4rem",
              fontWeight: 700, fontSize: "0.9rem", cursor: valido && !busy ? "pointer" : "not-allowed",
              transition: "all 0.15s",
            }}
          >
            {busy ? "Guardando…" : <><i className="fa-solid fa-circle-check mr-1"/>Confirmar cierre</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL: CORREGIR UBICACIÓN ─────────────────────────────
function ModalCorregirUbicacion({ solicitud, onClose, onSaved }) {
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [warn, setWarn] = useState(null); // {lat,lng} fuera de rango → confirmar
  const [success, setSuccess] = useState(null);
  const direccion = solicitud.direccion_texto || solicitud.titulo || solicitud.colonia || "";

  const abrirGoogleMaps = () => {
    const q = encodeURIComponent(direccion + ", Chihuahua, Chihuahua, México");
    window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, "_blank", "noopener");
  };

  const guardar = async (confirmarFueraRango = false) => {
    setBusy(true); setErr(null); setWarn(null);
    try {
      const { data } = await axios.post(
        `${API}/capturista/solicitud/${encodeURIComponent(solicitud.id)}/corregir-ubicacion`,
        { google_maps_link: link.trim(), confirmar_fuera_rango: confirmarFueraRango }
      );
      setSuccess(data);
      setTimeout(() => onSaved?.(), 1600);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message;
      if (status === 409) {
        const m = detail.match(/\((-?\d+\.\d+),\s*(-?\d+\.\d+)\)/);
        setWarn({ msg: detail, lat: m ? parseFloat(m[1]) : null, lng: m ? parseFloat(m[2]) : null });
      } else {
        setErr(detail);
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="modal-corregir">
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, background: "white", borderRadius: 14, padding: "1.25rem", width: "100%", maxHeight: "calc(100vh - 3rem)", overflowY: "auto" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-bold">
            <i className="fa-solid fa-location-dot text-rose-600 mr-1"/>
            Corregir ubicación
          </div>
          <button className="btn-ghost p-1" onClick={onClose} data-testid="btn-close-corregir"><i className="fa-solid fa-xmark"/></button>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-3">
          <div className="text-xs text-slate-500 uppercase font-bold mb-1">Reporte</div>
          <div className="mono text-sm font-bold text-slate-900">{solicitud.folio}</div>
          <div className="text-sm text-slate-700 mt-1" data-testid="modal-corregir-dir">{direccion || <em>Sin dirección</em>}</div>
          {solicitud.colonia && <div className="text-xs text-slate-500 mt-0.5">Col. {solicitud.colonia}</div>}
        </div>

        {success ? (
          <div className="rounded-md bg-emerald-50 border border-emerald-300 p-4 text-center" data-testid="corregir-ok">
            <i className="fa-solid fa-circle-check text-3xl text-emerald-600 mb-2"/>
            <div className="font-bold text-emerald-900">¡Ubicación corregida!</div>
            <div className="text-xs text-slate-600 mt-1 mono">{success.lat?.toFixed(5)}, {success.lng?.toFixed(5)}</div>
            <div className="text-sm mt-1">Zona calculada: <strong>{success.zona_calculada || "—"}</strong>
              {success.dist_km != null && <span className="text-xs text-slate-500"> · {success.dist_km} km del catálogo</span>}
            </div>
            <div className="text-xs text-slate-500 mt-1">Estatus: {success.estatus}</div>
          </div>
        ) : (
          <>
            <ol className="text-sm text-slate-600 list-decimal pl-5 space-y-1 mb-3">
              <li>Busca esta dirección en Google Maps.</li>
              <li>Copia el link de la barra de direcciones (o haz clic derecho sobre el pin y "¿Qué hay aquí?").</li>
              <li>Pégalo aquí abajo.</li>
            </ol>

            <button
              className="w-full mb-3 inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold rounded-md py-2 hover:bg-blue-700"
              onClick={abrirGoogleMaps}
              data-testid="btn-abrir-gmaps">
              <i className="fa-solid fa-up-right-from-square"/> Abrir Google Maps con esta dirección
            </button>

            <label className="text-xs font-semibold text-slate-700">Link de Google Maps o coordenadas</label>
            <textarea
              value={link}
              onChange={(e) => { setLink(e.target.value); setErr(null); setWarn(null); }}
              placeholder="https://www.google.com/maps/@28.65,-106.08,17z   ó   28.65, -106.08"
              rows={3}
              className="w-full mt-1 px-2 py-1.5 border border-slate-300 rounded text-sm mono"
              data-testid="input-gmaps-link"/>

            {err && (
              <div className="mt-2 p-2 rounded bg-rose-50 border border-rose-300 text-xs text-rose-800" data-testid="corregir-err">
                <i className="fa-solid fa-triangle-exclamation mr-1"/>{err}
              </div>
            )}

            {warn && (
              <div className="mt-2 p-3 rounded bg-amber-50 border border-amber-300 text-sm text-amber-900" data-testid="corregir-warn-fuera">
                <i className="fa-solid fa-triangle-exclamation mr-1"/>{warn.msg}
                <button
                  className="mt-2 block bg-amber-600 text-white px-3 py-1 rounded text-xs font-bold hover:bg-amber-700"
                  onClick={() => guardar(true)}
                  disabled={busy}
                  data-testid="btn-confirmar-fuera-rango">
                  Sí, esa ubicación es correcta
                </button>
              </div>
            )}

            <div className="flex gap-2 mt-4 justify-end">
              <button className="btn-ghost" onClick={onClose} disabled={busy} data-testid="btn-cancelar-corregir">Cancelar</button>
              <button
                className="btn-primary"
                onClick={() => guardar(false)}
                disabled={busy || !link.trim()}
                data-testid="btn-guardar-ubicacion">
                {busy ? "Guardando…" : (<><i className="fa-solid fa-check mr-1"/>Guardar ubicación</>)}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TareasHoy({ stats }) {
  if (!stats) return null;
  const total = stats.total_24h || 1;
  const pctPend = Math.round((stats.pendiente / total) * 100);
  const pctAten = Math.round((stats.atendido / total) * 100);
  return (
    <div className="card-base" data-testid="card-tareas">
      <div className="text-base font-bold mb-3">Mis tareas del día</div>
      <div className="mb-3">
        <div className="flex justify-between text-xs mb-1.5"><span className="text-slate-500">Progreso</span><span className="font-semibold">{pctAten}%</span></div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{width: `${pctAten}%`}}/></div>
      </div>
      <div>
        <div className="flex justify-between text-xs mb-1.5"><span className="text-slate-500">Pendientes</span><span className="font-semibold">{pctPend}%</span></div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-blue-500" style={{width: `${pctPend}%`}}/></div>
      </div>
    </div>
  );
}

// ─── MODAL VER DOC ─────────────────────────────────────────
function ModalDoc({ doc, onClose }) {
  const url = doc.documento_url;
  const nombre = doc.documento_nombre || "";
  const esImg = url && /\.(jpe?g|png|webp|gif)(\?|$)/i.test(url);
  const esPdf = url && /\.pdf(\?|$)/i.test(url);
  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="modal-doc">
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="mono font-bold">{doc.folio}</div>
            <div className="text-sm text-slate-600">{nombre}</div>
          </div>
          <button onClick={onClose} className="iconbtn" data-testid="btn-close-modal"><i className="fa-solid fa-xmark"/></button>
        </div>
        {url ? (
          esImg ? (
            <img src={url} alt={nombre} className="w-full rounded-lg border border-slate-200" data-testid="modal-doc-img"/>
          ) : esPdf ? (
            <iframe src={url} title={nombre} className="w-full rounded-lg border border-slate-200" style={{ height: 520 }} data-testid="modal-doc-pdf"/>
          ) : (
            <a href={url} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid="modal-doc-link">
              <i className="fa-solid fa-download"/> Descargar documento
            </a>
          )
        ) : (
          <div className="bg-slate-100 rounded-lg p-10 text-center text-slate-500" data-testid="modal-placeholder">
            <i className="fa-solid fa-file-circle-xmark text-6xl mb-4 text-slate-400"/>
            <div className="text-lg font-semibold">Sin documento adjunto</div>
            <div className="text-sm mt-2 text-slate-500">Este registro no tiene archivo respaldado en Cloudinary.</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Mini({ label, val, color, icon }) {
  return (
    <div className="card-base" style={{padding: "0.85rem"}}>
      <i className={`fa-solid ${icon} text-sm`} style={{color}}/>
      <div className="text-2xl font-bold mt-1" style={{color}}>{val}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}


/* ───────────────────────────────────────────────────────────
   Tab: Captura de intervención (plan B WhatsApp)
   ─────────────────────────────────────────────────────────── */
function TabCapturaIntervencion({ onCreada }) {
  const hoy = new Date().toISOString().slice(0, 10);
  const [fecha, setFecha] = useState(hoy);
  const [turno, setTurno] = useState("PRIMER_TURNO");
  const [pipas, setPipas] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [pipaNumero, setPipaNumero] = useState("");
  const [choferId, setChoferId] = useState("");
  const [tipoServicio, setTipoServicio] = useState("PIPA_TRATADA");
  const [obs, setObs] = useState("");
  const [fotos, setFotos] = useState([]);    // base64 data URLs
  const [espacio, setEspacio] = useState(null); // {id, id_oficial, nombre, zona_id}
  const [buscar, setBuscar] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [mostrarNuevo, setMostrarNuevo] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pipas`).then(r => setPipas(r.data || []));
    axios.get(`${API}/usuarios?rol=chofer`).then(r => setChoferes(r.data || [])).catch(() => setChoferes([]));
  }, []);

  // Buscar espacios al teclear (debounce ligero)
  useEffect(() => {
    if (!buscar || buscar.length < 2) { setResultados([]); return; }
    setBuscando(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/espacios`, { params: { q: buscar, limit: 12 } });
        setResultados(r.data || []);
      } catch { setResultados([]); }
      finally { setBuscando(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [buscar]);

  const onFotoFiles = (e) => {
    const files = [...(e.target.files || [])].slice(0, 3 - fotos.length);
    files.forEach(f => {
      const reader = new FileReader();
      reader.onload = () => setFotos(prev => [...prev, reader.result].slice(0, 3));
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };
  const quitarFoto = (i) => setFotos(prev => prev.filter((_, k) => k !== i));

  const guardar = async () => {
    if (!pipaNumero) { toast.error("Selecciona la pipa"); return; }
    if (!espacio)    { toast.error("Selecciona el lugar"); return; }
    setBusy(true);
    try {
      const payload = {
        fecha, turno, pipa_numero: pipaNumero,
        chofer_id: choferId || null,
        tipo_servicio: tipoServicio,
        fotos, observaciones: obs || null,
      };
      // Si el espacio fue creado nuevo (no existe en BD aún), enviar espacio_nuevo
      if (espacio.__temporal) {
        payload.espacio_nuevo = {
          nombre: espacio.nombre,
          zona_id: espacio.zona_id,
          tipo_espacio: espacio.tipo_espacio,
          lat: espacio.lat,
          lng: espacio.lng,
        };
      } else {
        payload.espacio_id = espacio.id;
      }
      const r = await axios.post(`${API}/capturista/intervencion`, payload);
      const prov = r.data.espacio_provisional_creado ? " (lugar provisional)" : "";
      toast.success(`Intervención guardada · pipa ${pipaNumero} en ${espacio.nombre}${prov}`);
      // Reset rápido (mantiene fecha y turno)
      setEspacio(null); setBuscar(""); setResultados([]);
      setFotos([]); setObs(""); setChoferId("");
      if (onCreada) onCreada();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-base" style={{padding:"1.25rem"}} data-testid="tab-intervencion">
      <div className="flex items-center gap-3 mb-4">
        <div style={{background:"#0EA5E9", color:"white", padding:"0.5rem 0.75rem", borderRadius:"10px"}}>
          <i className="fa-solid fa-truck-droplet"/>
        </div>
        <div>
          <h2 className="text-lg font-bold m-0">Captura de intervención</h2>
          <p className="text-xs text-slate-500 m-0">Plan B operativo · cuando el chofer reportó por WhatsApp y no usó la app</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Fecha *</label>
          <input type="date" className="cap-input w-full" value={fecha} onChange={e=>setFecha(e.target.value)} data-testid="int-fecha"/>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Turno *</label>
          <select className="cap-input w-full" value={turno} onChange={e=>setTurno(e.target.value)} data-testid="int-turno">
            <option value="PRIMER_TURNO">PRIMER TURNO</option>
            <option value="SEGUNDO_TURNO">SEGUNDO TURNO</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Tipo servicio</label>
          <select className="cap-input w-full" value={tipoServicio} onChange={e=>setTipoServicio(e.target.value)} data-testid="int-tipo">
            <option value="PIPA_TRATADA">Agua tratada</option>
            <option value="PIPA_POTABLE">Agua potable</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Pipa *</label>
          <select className="cap-input w-full" value={pipaNumero} onChange={e=>setPipaNumero(e.target.value)} data-testid="int-pipa">
            <option value="">— Selecciona pipa —</option>
            {pipas.map(p => (
              <option key={p.id_pipa} value={p.id_pipa}>
                #{p.id_pipa} · {p.categoria || ""} · {p.zona_asignada || "—"}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Chofer (opcional)</label>
          <select className="cap-input w-full" value={choferId} onChange={e=>setChoferId(e.target.value)} data-testid="int-chofer">
            <option value="">— Sin especificar —</option>
            {choferes.map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.n_empleado})</option>)}
          </select>
        </div>
      </div>

      {/* Lugar */}
      <div className="mb-3">
        <label className="text-xs font-semibold text-slate-600 block mb-1">Lugar *</label>
        {espacio ? (
          <div className="cap-lugar-chip" data-testid="int-lugar-elegido">
            <i className="fa-solid fa-location-dot text-emerald-600"/>
            <div className="flex-1">
              <div className="font-semibold">{espacio.nombre}</div>
              <div className="text-xs text-slate-500">
                <span className="font-mono">{espacio.id_oficial}</span> · {espacio.zona_id}
                {espacio.es_provisional && <span className="ml-2 pill-tiny" style={{background:"#FED7AA",color:"#7C2D12",fontWeight:700}}>PROVISIONAL</span>}
              </div>
            </div>
            <button className="btn-ghost-sm" onClick={() => { setEspacio(null); setBuscar(""); }} data-testid="int-lugar-cambiar">Cambiar</button>
          </div>
        ) : (
          <>
            <input className="cap-input w-full" type="text" placeholder="Busca por nombre, id oficial o colonia…"
              value={buscar} onChange={e=>setBuscar(e.target.value)} data-testid="int-lugar-buscar"/>
            {buscando && <div className="text-xs text-slate-400 mt-1">Buscando…</div>}
            {!buscando && buscar.length >= 2 && (
              <div className="cap-lugar-results" data-testid="int-lugar-results">
                {resultados.slice(0, 8).map(r => (
                  <button key={r.id} type="button" className="cap-lugar-row"
                    onClick={() => { setEspacio(r); setBuscar(""); setResultados([]); }}
                    data-testid={`int-lugar-opt-${r.id_oficial}`}>
                    <div>
                      <div className="font-medium">{r.nombre}</div>
                      <div className="text-xs text-slate-500"><span className="font-mono">{r.id_oficial}</span> · {r.zona_id}</div>
                    </div>
                    <i className="fa-solid fa-chevron-right text-slate-400"/>
                  </button>
                ))}
                {resultados.length === 0 && (
                  <div className="text-center py-3 text-sm text-slate-500">
                    No hay resultados.
                    <button type="button" className="btn-primary ml-2" onClick={() => setMostrarNuevo(true)} data-testid="int-btn-registrar-nuevo" style={{padding:"0.3rem 0.7rem", fontSize:"0.8rem"}}>
                      <i className="fa-solid fa-plus mr-1"/>Registrar lugar nuevo
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="mt-1 text-xs text-slate-500">
              ¿No aparece?{" "}
              <button type="button" className="text-sky-600 underline" onClick={() => setMostrarNuevo(true)} data-testid="int-link-nuevo">
                Registrar lugar nuevo
              </button>
            </div>
          </>
        )}
      </div>

      {/* Fotos */}
      <div className="mb-3">
        <label className="text-xs font-semibold text-slate-600 block mb-1">Fotos de respaldo (máx 3)</label>
        <div className="flex gap-2 flex-wrap items-center">
          {fotos.map((f, i) => (
            <div key={i} className="cap-foto-mini" data-testid={`int-foto-${i}`}>
              <img src={f} alt={`foto-${i}`}/>
              <button type="button" onClick={() => quitarFoto(i)} className="cap-foto-x" data-testid={`int-foto-quitar-${i}`}><i className="fa-solid fa-xmark"/></button>
            </div>
          ))}
          {fotos.length < 3 && (
            <label className="cap-foto-add" data-testid="int-foto-add">
              <i className="fa-solid fa-camera"/> Agregar
              <input type="file" accept="image/*" multiple onChange={onFotoFiles} style={{display:"none"}}/>
            </label>
          )}
        </div>
      </div>

      <div className="mb-3">
        <label className="text-xs font-semibold text-slate-600 block mb-1">Observaciones (opcional)</label>
        <textarea className="cap-input w-full" rows={2} maxLength={500} value={obs} onChange={e=>setObs(e.target.value)} data-testid="int-obs"
          placeholder="Detalles relevantes de la intervención (ej: número de llenadas, problemas)"/>
      </div>

      <div className="flex justify-end gap-2">
        <button className="btn-primary" disabled={busy} onClick={guardar} data-testid="int-btn-guardar">
          {busy ? "Guardando…" : <><i className="fa-solid fa-floppy-disk mr-1"/>Guardar intervención</>}
        </button>
      </div>

      {mostrarNuevo && (
        <ModalLugarNuevo
          nombreInicial={buscar}
          onClose={() => setMostrarNuevo(false)}
          onCreado={(esp) => { setEspacio(esp); setMostrarNuevo(false); setBuscar(""); setResultados([]); }}
        />
      )}
    </div>
  );
}

function ModalLugarNuevo({ nombreInicial, onClose, onCreado }) {
  const [nombre, setNombre] = useState(nombreInicial || "");
  const [zona, setZona] = useState("Z1");
  const [tipo, setTipo] = useState("OTRO");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");

  const aceptar = () => {
    if (!nombre.trim() || !zona) { toast.error("Nombre y zona son obligatorios"); return; }
    // No persistimos aún el espacio; lo enviamos junto con la intervención.
    // Pero como el componente padre espera un objeto "elegido", devolvemos un objeto temporal.
    onCreado({
      __temporal: true,
      nombre: nombre.trim(),
      zona_id: zona,
      tipo_espacio: tipo,
      lat: lat ? parseFloat(lat) : null,
      lng: lng ? parseFloat(lng) : null,
      id_oficial: `(nuevo · ${zona})`,
      es_provisional: true,
    });
  };

  return (
    <div className="mp-bg" onClick={onClose} data-testid="modal-lugar-nuevo">
      <div className="mp-card" onClick={e=>e.stopPropagation()} style={{maxWidth:480}}>
        <header className="mp-head">
          <div className="mp-head-text">
            <h2 className="mp-title"><i className="fa-solid fa-plus mr-2"/>Registrar lugar nuevo</h2>
            <div className="mp-id">Quedará marcado como PROVISIONAL hasta validación</div>
          </div>
          <button onClick={onClose} className="mp-x"><i className="fa-solid fa-xmark"/></button>
        </header>
        <div className="mp-body" style={{display:"flex", flexDirection:"column", gap:"0.75rem"}}>
          <div>
            <label className="mp-validar-lbl">Nombre *</label>
            <input type="text" className="mp-select" value={nombre} onChange={e=>setNombre(e.target.value)} data-testid="nuevo-nombre" placeholder="Ej: Tinaco calle Tomate"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mp-validar-lbl">Zona *</label>
              <select className="mp-select" value={zona} onChange={e=>setZona(e.target.value)} data-testid="nuevo-zona">
                {Array.from({length:14},(_,i)=>`Z${i+1}`).map(z=><option key={z} value={z}>{z}</option>)}
              </select>
            </div>
            <div>
              <label className="mp-validar-lbl">Tipo</label>
              <select className="mp-select" value={tipo} onChange={e=>setTipo(e.target.value)} data-testid="nuevo-tipo">
                <option value="PARQUE">Parque</option>
                <option value="CAMELLON">Camellón</option>
                <option value="TINACO">Tinaco</option>
                <option value="OTRO">Otro</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mp-validar-lbl">Lat (opcional)</label>
              <input type="number" step="0.000001" className="mp-select" value={lat} onChange={e=>setLat(e.target.value)} data-testid="nuevo-lat"/>
            </div>
            <div>
              <label className="mp-validar-lbl">Lng (opcional)</label>
              <input type="number" step="0.000001" className="mp-select" value={lng} onChange={e=>setLng(e.target.value)} data-testid="nuevo-lng"/>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Si no conoces el GPS, déjalo vacío. Se completará después por el equipo de campo.
          </p>
        </div>
        <footer className="mp-foot">
          <button className="btn-primary" onClick={aceptar} data-testid="nuevo-confirmar">Usar este lugar</button>
          <button className="btn-ghost" onClick={onClose} style={{padding:"0.55rem 1rem"}}>Cancelar</button>
        </footer>
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────
   Tab: Captura unificada de actividad (reemplaza Intervención + Oficio)
   Yanett — desde 2026-10-01 sin bot WhatsApp.
   Octavio 11-sep-2026.
   ─────────────────────────────────────────────────────────── */

const CATEGORIAS_LUGAR = [
  { k: "parque",         lbl: "Parque",           icon: "fa-tree" },
  { k: "camellon",       lbl: "Camellón",          icon: "fa-road" },
  { k: "escuela",        lbl: "Escuela",           icon: "fa-school" },
  { k: "oficinas",       lbl: "Oficinas",          icon: "fa-building" },
  { k: "espacio_publico",lbl: "Espacio público",   icon: "fa-landmark" },
  { k: "garza",          lbl: "Garza / Llenado",   icon: "fa-fill-drip" },
  { k: "taller",         lbl: "Taller",            icon: "fa-wrench" },
  { k: "bomberos",       lbl: "Bomberos",          icon: "fa-fire-extinguisher" },
  { k: "aljibe",         lbl: "Aljibe",            icon: "fa-database" },
  { k: "tinaco",         lbl: "Tinaco",            icon: "fa-tank-water" },
  { k: "fuente",         lbl: "Fuente",            icon: "fa-droplet" },
];

const TIPOS_ATENCION = [
  { k: "ordinaria", lbl: "Ordinario",  bg: "#DCFCE7", color: "#166534" },
  { k: "crc",       lbl: "CRC",        bg: "#DBEAFE", color: "#1E40AF" },
  { k: "oficio",    lbl: "Oficio",     bg: "#FEF3C7", color: "#92400E" },
  { k: "emergencia",lbl: "Emergencia", bg: "#FEE2E2", color: "#991B1B" },
];

// API ya definida al tope del archivo como `${process.env.REACT_APP_BACKEND_URL}/api`

function TabCapturaUnificada({ onCreada }) {
  const [pipas, setPipas] = useState([]);
  const [pipaId, setPipaId] = useState("");
  const [tipoAguaAuto, setTipoAguaAuto] = useState(null); // derivado al seleccionar pipa
  const [nombreChofer, setNombreChofer] = useState("");
  const [categoria, setCategoria] = useState("");
  const [tipoAtencion, setTipoAtencion] = useState("ordinaria");
  const [buscarTxt, setBuscarTxt] = useState("");
  const [sugerencias, setSugerencias] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [espacioId, setEspacioId] = useState(null);
  const [nombreLugar, setNombreLugar] = useState("");
  const [historial, setHistorial] = useState([]);
  const [folio, setFolio] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [fotos, setFotos] = useState([]); // data URLs
  const [fechaIntervencion, setFechaIntervencion] = useState(
    () => new Date().toLocaleDateString("en-CA") // YYYY-MM-DD (hoy por defecto)
  );
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // resultado dry_run

  // Cargar pipas al montar
  useEffect(() => {
    axios.get(`${API}/pipas`).then(r => setPipas(r.data || [])).catch(() => {});
  }, []);

  // Bloquear comportamiento default del navegador al soltar archivos en cualquier
  // parte de la página (sin esto el browser abre la imagen en pestaña nueva
  // cuando el drop cae fuera del área de fotos o cuando WhatsApp entrega un link).
  useEffect(() => {
    const block = e => e.preventDefault();
    document.addEventListener("dragover", block);
    document.addEventListener("drop", block);
    return () => {
      document.removeEventListener("dragover", block);
      document.removeEventListener("drop", block);
    };
  }, []);

  // Derivar tipo de agua cuando cambia la pipa
  useEffect(() => {
    if (!pipaId) { setTipoAguaAuto(null); return; }
    const pipa = pipas.find(p => String(p.id_pipa) === String(pipaId));
    if (!pipa) { setTipoAguaAuto(null); return; }
    const esZonalTratada = pipa.tipo === "ZONAL_TRATADA";
    const tipoAgua = pipa.tipo_agua || (esZonalTratada ? "TRATADA" : "POTABLE");
    setTipoAguaAuto(tipoAgua.toUpperCase());
  }, [pipaId, pipas]);

  // Pre-selección de tipo_atencion al elegir bomberos
  useEffect(() => {
    if (categoria === "bomberos") setTipoAtencion("emergencia");
  }, [categoria]);

  // Autocompletado de lugar (debounce 350ms)
  useEffect(() => {
    if (!buscarTxt || buscarTxt.length < 2) { setSugerencias([]); return; }
    setBuscando(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(
          `${API}/capturista/buscar-espacio`,
          { params: { q: buscarTxt } }
        );
        setSugerencias(data || []);
      } catch { setSugerencias([]); }
      finally { setBuscando(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [buscarTxt]);

  // Historial cuando se elige un espacio del catálogo
  useEffect(() => {
    if (!espacioId) { setHistorial([]); return; }
    axios.get(`${API}/capturista/historial-lugar`, {
      params: { espacio_id: espacioId, limite: 4 }
    }).then(r => setHistorial(r.data || [])).catch(() => setHistorial([]));
  }, [espacioId]);

  const elegirSugerencia = (s) => {
    setEspacioId(s.espacio_id);
    setNombreLugar(s.nombre);
    setBuscarTxt("");
    setSugerencias([]);
  };

  const limpiarLugar = () => {
    setEspacioId(null);
    setNombreLugar("");
    setBuscarTxt("");
    setSugerencias([]);
    setHistorial([]);
  };

  const onFotoFiles = (e) => {
    const files = [...(e.target.files || [])].slice(0, 3 - fotos.length);
    files.forEach(f => {
      const reader = new FileReader();
      reader.onload = () => setFotos(prev => [...prev, reader.result].slice(0, 3));
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const construirBody = () => ({
    pipa_id: pipaId,
    chofer_nombre: nombreChofer,
    categoria_lugar: categoria,
    espacio_id: espacioId || null,
    nombre: nombreLugar,
    tipo_atencion: tipoAtencion,
    folio: folio.trim() || null,
    descripcion: descripcion.trim() || null,
    fotos_data: fotos,
    fecha_intervencion: fechaIntervencion,
  });

  const handlePreview = async () => {
    if (!pipaId)       { toast.error("Selecciona la pipa"); return; }
    if (!nombreChofer.trim()) { toast.error("Escribe el nombre del chofer"); return; }
    if (!categoria)    { toast.error("Selecciona la categoría del lugar"); return; }
    if (!nombreLugar.trim()) { toast.error("Escribe o selecciona el lugar"); return; }
    if (["crc","oficio"].includes(tipoAtencion) && !folio.trim()) {
      toast.error("El folio es obligatorio para CRC u Oficio");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/capturista/captura-unificada?dry_run=true`,
        construirBody()
      );
      setPreview(data);
    } catch (e) {
      toast.error("Error en preview: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const handleGuardar = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/capturista/captura-unificada?dry_run=false`,
        construirBody()
      );
      const tipoAgua = data.tipo_agua_derivada === "TRATADA" ? "tratada" : "potable";
      toast.success(`✓ Registro guardado — agua ${tipoAgua}. Ya puedes capturar el siguiente.`);
      setPreview(null);
      // Reset completo
      setPipaId(""); setNombreChofer(""); setCategoria(""); setNombreLugar(""); setEspacioId(null);
      setBuscarTxt(""); setSugerencias([]); setHistorial([]);
      setTipoAtencion("ordinaria"); setFolio(""); setDescripcion(""); setFotos([]);
      setFechaIntervencion(new Date().toLocaleDateString("en-CA"));
      onCreada?.();
    } catch (e) {
      toast.error("Error al guardar: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const badgeAtencion = TIPOS_ATENCION.find(t => t.k === tipoAtencion);
  const folioRequerido = ["crc","oficio"].includes(tipoAtencion);

  return (
    <div className="card-base" data-testid="tab-captura-unificada">
      {/* Cabecera */}
      <div className="flex items-center gap-3 mb-5">
        <div style={{background:"#0EA5E9", color:"white", padding:"0.5rem 0.75rem", borderRadius:"10px"}}>
          <i className="fa-solid fa-truck-droplet"/>
        </div>
        <div>
          <h2 className="text-lg font-bold m-0">Captura de actividad</h2>
          <p className="text-xs text-slate-500 m-0">Intervenciones, CRC, Oficios y Emergencias — captura manual desde 2026-10-01</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Pipa */}
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Pipa / Unidad *</label>
          <select className="cap-input w-full" value={pipaId} onChange={e => setPipaId(e.target.value)} data-testid="cu-pipa">
            <option value="">— Selecciona la pipa —</option>
            {pipas.map(p => (
              <option key={p.id_pipa} value={p.id_pipa}>
                #{p.id_pipa}{p.categoria ? ` · ${p.categoria}` : ""}{p.zona_asignada ? ` · ${p.zona_asignada}` : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Tipo de agua (solo lectura, automático) */}
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Tipo de agua</label>
          <div className="cap-input w-full flex items-center gap-2" style={{background:"#F8FAFC", cursor:"default"}} data-testid="cu-tipo-agua">
            {tipoAguaAuto ? (
              <>
                <span className="font-bold" style={{color: tipoAguaAuto === "TRATADA" ? "#047857" : "#1E40AF"}}>
                  {tipoAguaAuto === "TRATADA" ? "Tratada" : "Potable"}
                </span>
                <span className="text-xs text-slate-400">· derivado de la pipa</span>
              </>
            ) : (
              <span className="text-slate-400 text-sm">Selecciona la pipa primero</span>
            )}
          </div>
        </div>

        {/* Chofer */}
        <div className="md:col-span-2">
          <label className="text-xs font-semibold text-slate-600 block mb-1">Nombre del chofer *</label>
          <input className="cap-input w-full" type="text" placeholder="Escribe el nombre completo"
            value={nombreChofer} onChange={e => setNombreChofer(e.target.value)} data-testid="cu-chofer"/>
        </div>

        {/* Fecha de la intervención */}
        <div className="md:col-span-2">
          <label className="text-xs font-semibold text-slate-600 block mb-1">
            Fecha de la intervención *
            <span className="ml-2 text-slate-400 font-normal normal-case">
              (puede ser distinta al día de hoy si la capturas tarde)
            </span>
          </label>
          <input
            className="cap-input"
            type="date"
            value={fechaIntervencion}
            onChange={e => setFechaIntervencion(e.target.value)}
            style={{ width: "auto", minWidth: 180 }}
            data-testid="cu-fecha-intervencion"
          />
        </div>
      </div>

      {/* Categoría de lugar */}
      <div className="mb-4">
        <label className="text-xs font-semibold text-slate-600 block mb-2">Categoría del lugar *</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="cu-categorias">
          {CATEGORIAS_LUGAR.map(c => (
            <button key={c.k} type="button"
              className={`btn-seleccion w-full justify-start ${categoria === c.k ? "activo" : ""}`}
              onClick={() => setCategoria(c.k)}
              data-testid={`cu-cat-${c.k}`}>
              <i className={`fa-solid ${c.icon} fa-sm`}/>{c.lbl}
            </button>
          ))}
        </div>
      </div>

      {/* Origen / tipo de atención */}
      <div className="mb-4">
        <label className="text-xs font-semibold text-slate-600 block mb-2">Origen / tipo de atención *</label>
        <div className="flex gap-2 flex-wrap" data-testid="cu-atencion">
          {TIPOS_ATENCION.map(t => {
            const colorClass = {
              ordinaria: "activo-verde",
              crc: "activo",
              oficio: "activo-amber",
              emergencia: "activo-rojo",
            }[t.k] || "activo";
            return (
              <button key={t.k} type="button"
                className={`btn-seleccion ${tipoAtencion === t.k ? colorClass : ""}`}
                style={{ padding: "0.65rem 1.1rem", minHeight: "unset" }}
                onClick={() => setTipoAtencion(t.k)}
                data-testid={`cu-ta-${t.k}`}>
                {t.lbl}
              </button>
            );
          })}
        </div>
        {categoria === "bomberos" && tipoAtencion !== "emergencia" && (
          <div className="mt-1 text-xs text-amber-700">
            <i className="fa-solid fa-triangle-exclamation mr-1"/>Bomberos se recomienda como Emergencia
          </div>
        )}
      </div>

      {/* Folio (solo si crc u oficio) */}
      {folioRequerido && (
        <div className="mb-4">
          <label className="text-xs font-semibold text-slate-600 block mb-1">
            Folio <span className="text-rose-600">*</span>
            <span className="font-normal text-slate-400 ml-1">
              ({tipoAtencion === "crc" ? "número CRC de Atención Ciudadana" : "número de oficio"})
            </span>
          </label>
          <input className="cap-input w-full font-mono" type="text"
            placeholder={tipoAtencion === "crc" ? "CRC677034" : "OF-2026-001"}
            value={folio} onChange={e => setFolio(e.target.value)} data-testid="cu-folio"/>
        </div>
      )}

      {/* Lugar — buscador con autocompletado */}
      <div className="mb-4">
        <label className="text-xs font-semibold text-slate-600 block mb-1">Lugar *</label>
        {espacioId ? (
          <div className="cap-lugar-chip" data-testid="cu-lugar-elegido">
            <i className="fa-solid fa-location-dot text-emerald-600"/>
            <div className="flex-1">
              <div className="font-semibold">{nombreLugar}</div>
              <div className="text-xs text-slate-400">ID: {espacioId}</div>
            </div>
            <button className="btn-ghost-sm" onClick={limpiarLugar} data-testid="cu-lugar-cambiar">Cambiar</button>
          </div>
        ) : (
          <>
            <div className="relative">
              <input className="cap-input w-full" type="text"
                placeholder="Escribe o arrastra texto desde WhatsApp…"
                value={buscarTxt}
                onChange={e => { setBuscarTxt(e.target.value); setNombreLugar(e.target.value); setEspacioId(null); }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  const texto = e.dataTransfer.getData("text/plain") || e.dataTransfer.getData("text");
                  if (!texto) return;
                  // Limpieza: quitar saltos de línea, colapsar espacios,
                  // quitar prefijos tipo "pipa.XXXXX N" que WhatsApp suele incluir
                  const limpio = texto
                    .replace(/\r?\n|\r/g, " ")
                    .replace(/\bpipa[\s.]?\d+\b/gi, "")
                    .replace(/\b\d{3,6}\b/g, "")  // números sueltos (IDs, pilas)
                    .replace(/\s{2,}/g, " ")
                    .trim();
                  if (!limpio) return;
                  setBuscarTxt(limpio);
                  setNombreLugar(limpio);
                  setEspacioId(null);
                }}
                data-testid="cu-lugar-buscar"/>
              {buscando && <div className="absolute right-3 top-2.5 text-slate-400 text-xs">buscando…</div>}
            </div>
            {sugerencias.length > 0 && (
              <div className="cap-lugar-results" data-testid="cu-lugar-results">
                {sugerencias.map(s => (
                  <button key={s.espacio_id} type="button" className="cap-lugar-row"
                    onClick={() => elegirSugerencia(s)}
                    data-testid={`cu-sug-${s.espacio_id}`}>
                    <div>
                      <div className="font-medium">{s.nombre}</div>
                      <div className="text-xs text-slate-500">{s.tipo_espacio}</div>
                    </div>
                    <i className="fa-solid fa-chevron-right text-slate-400"/>
                  </button>
                ))}
              </div>
            )}
            {!buscando && buscarTxt.length >= 2 && sugerencias.length === 0 && (
              <div className="text-xs text-slate-500 mt-1">
                <i className="fa-solid fa-info-circle mr-1"/>No hay coincidencias en el catálogo — se guardará como texto libre.
              </div>
            )}
          </>
        )}
      </div>

      {/* Historial de riego en este punto */}
      {historial.length > 0 && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="cu-historial">
          <div className="text-xs font-semibold text-slate-600 mb-2">
            <i className="fa-solid fa-clock-rotate-left mr-1"/>Historial de riego en este punto
          </div>
          <div className="space-y-1.5">
            {historial.map((h, i) => {
              const ta = TIPOS_ATENCION.find(t => t.k === h.tipo_atencion);
              return (
                <div key={i} className="flex items-center gap-2 text-xs" data-testid={`cu-hist-${i}`}>
                  {h.foto_url ? (
                    <img src={h.foto_url} alt="evidencia" className="w-8 h-8 object-cover rounded" style={{flexShrink:0}}/>
                  ) : (
                    <div className="w-8 h-8 rounded bg-slate-200 flex items-center justify-center" style={{flexShrink:0}}>
                      <i className="fa-solid fa-image text-slate-400 fa-xs"/>
                    </div>
                  )}
                  <div>
                    <span className="font-semibold text-slate-700">{h.fecha_iso}</span>
                    <span className="text-slate-500 ml-1">Pipa {h.pipa_id}</span>
                    {h.chofer_nombre && <span className="text-slate-500 ml-1">· {h.chofer_nombre}</span>}
                  </div>
                  {ta && (
                    <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold"
                      style={{background: ta.bg, color: ta.color}}>{ta.lbl}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Evidencia fotográfica */}
      <div className="mb-4">
        <label className="text-xs font-semibold text-slate-600 block mb-1">Evidencia fotográfica (máx 3)</label>
        <div
          className="flex gap-2 flex-wrap items-center"
          onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={e => {
            e.preventDefault(); e.stopPropagation();
            // DEBUG: ayuda a diagnosticar qué entrega WhatsApp Web
            console.log("[drop] types:", [...(e.dataTransfer.types || [])]);
            console.log("[drop] files:", e.dataTransfer.files.length, "items:", e.dataTransfer.items.length);
            const files = [...(e.dataTransfer.files || [])].filter(f => f.type.startsWith("image/")).slice(0, 3 - fotos.length);
            if (files.length === 0) {
              // WhatsApp a veces entrega URL en vez de File — loguear para diagnóstico
              const url = e.dataTransfer.getData("text/uri-list") || e.dataTransfer.getData("text/plain");
              console.log("[drop] sin archivos — URL recibida:", url);
            }
            files.forEach(f => {
              const reader = new FileReader();
              reader.onload = () => setFotos(prev => [...prev, reader.result].slice(0, 3));
              reader.readAsDataURL(f);
            });
          }}
        >
          {fotos.map((f, i) => (
            <div key={i} className="cap-foto-mini" data-testid={`cu-foto-${i}`}>
              <img src={f} alt={`foto-${i}`}/>
              <button type="button" className="cap-foto-x" onClick={() => setFotos(prev => prev.filter((_,k) => k !== i))}
                data-testid={`cu-foto-quitar-${i}`}><i className="fa-solid fa-xmark"/></button>
            </div>
          ))}
          {fotos.length < 3 && (
            <label className="cap-foto-add" data-testid="cu-foto-add">
              <i className="fa-solid fa-camera"/> Agregar o arrastra aquí
              <input type="file" accept="image/*" multiple onChange={onFotoFiles} style={{display:"none"}}/>
            </label>
          )}
        </div>
      </div>

      {/* Notas */}
      <div className="mb-5">
        <label className="text-xs font-semibold text-slate-600 block mb-1">Notas / observaciones (opcional)</label>
        <textarea className="cap-input w-full" rows={2} maxLength={500} placeholder="Detalles adicionales…"
          value={descripcion} onChange={e => setDescripcion(e.target.value)} data-testid="cu-desc" style={{resize:"vertical"}}/>
      </div>

      {/* Preview resultado dry_run */}
      {preview && (
        <div className="mb-5 rounded-lg border border-sky-200 bg-sky-50 p-4" data-testid="cu-preview">
          <div className="text-sm font-bold text-sky-900 mb-2">
            <i className="fa-solid fa-eye mr-1"/>Preview del documento a guardar
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs mb-2">
            {[
              ["Pipa", preview.documento_preview?.pipa_id],
              ["Tipo agua", preview.documento_preview?.tipo_agua],
              ["Categoría", preview.documento_preview?.categoria_lugar],
              ["Tipo atención", preview.documento_preview?.tipo_atencion],
              ["Lugar", preview.documento_preview?.nombre],
              ["Folio", preview.documento_preview?.folio || "—"],
              ["Espacio ID", preview.documento_preview?.espacio_id || "texto libre"],
              ["Fotos", `${preview.documento_preview?.fotos_count}`],
              ["Fecha intervención", preview.documento_preview?.fecha_intervencion || "—"],
              ["Pipa encontrada", preview.pipa_encontrada ? "✓ Sí" : "✗ No (fallback POTABLE)"],
            ].map(([k, v]) => (
              <div key={k} className="bg-white rounded px-2 py-1 border border-sky-100">
                <div className="text-[10px] uppercase text-sky-600 font-bold">{k}</div>
                <div className="font-semibold text-slate-800 truncate">{v}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <button className="btn-primary" disabled={busy} onClick={handleGuardar} data-testid="cu-btn-confirmar">
              {busy ? "Guardando…" : <><i className="fa-solid fa-check mr-1"/>Confirmar y guardar</>}
            </button>
            <button className="btn-ghost" onClick={() => setPreview(null)} data-testid="cu-btn-cancelar-preview">Editar</button>
            {folioRequerido && folio.trim() && (
              <button
                className="ml-auto text-xs flex items-center gap-1 px-3 py-1.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
                style={{background:"white"}}
                onClick={async () => {
                  const url = `${window.location.origin}/evidencia/${encodeURIComponent(folio.trim())}`;
                  try {
                    if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
                    else { const ta=document.createElement("textarea"); ta.value=url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); }
                    toast.success("Link de evidencia copiado");
                  } catch { toast.error("Link: " + url); }
                }}
                data-testid="cu-btn-copiar-link">
                <i className="fa-solid fa-link"/>Copiar link evidencia
              </button>
            )}
          </div>
        </div>
      )}

      {/* Botón principal */}
      {!preview && (
        <div className="flex justify-end">
          <button className="btn-primary" disabled={busy} onClick={handlePreview} data-testid="cu-btn-preview">
            {busy ? "Verificando…" : <><i className="fa-solid fa-magnifying-glass mr-1"/>Previsualizar captura</>}
          </button>
        </div>
      )}
    </div>
  );
}


// ─── TAB: PARQUES CONTRATADOS 2026 ──────────────────────────
const MAPA_EMBED_URL =
  "https://www.google.com/maps/d/u/0/embed?mid=1gPPV-qp7s6bFrjVQbjPdfybjX2pKi3A&ll=28.652715999999998,-106.03392&z=18";
const MAPA_VIEWER_URL =
  "https://www.google.com/maps/d/u/0/viewer?mid=1gPPV-qp7s6bFrjVQbjPdfybjX2pKi3A";

export function TabParquesContratos() {
  return (
    <section className="card-base" data-testid="tab-parques-contratos">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-lg font-bold">Parques Contratados 2026</div>
          <div className="text-sm text-slate-500 mt-0.5">
            Consulta aquí si un parque o espacio ya está contratado para mantenimiento, antes de asignar una intervención.
          </div>
        </div>
        <a
          href={MAPA_VIEWER_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50 transition"
          style={{ background: "white", whiteSpace: "nowrap" }}
          data-testid="btn-abrir-mapa-contratos"
        >
          <i className="fa-solid fa-up-right-from-square"/> Abrir en Google Maps
        </a>
      </div>

      <div style={{ position: "relative", width: "100%", minHeight: 600, borderRadius: 10, overflow: "hidden", border: "1px solid #E2E8F0" }}>
        <iframe
          title="Parques Contratos 2026"
          src={MAPA_EMBED_URL}
          width="100%"
          height="600"
          style={{ border: 0, display: "block", minHeight: 600 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          data-testid="iframe-parques-contratos"
        />
      </div>
    </section>
  );
}
